import { tenantScopeHeaders } from './tenantHost';
import type {
  AuthSession,
  OnboardingStep,
  AttributeDefinition,
  CatalogAttributeFilters,
  CatalogFilterAttribute,
  EditorialCollectionPayload,
  CatalogProductDetail,
  CatalogProductsPage,
  CatalogPromotionSummary,
  Category,
  Product,
  ProductPriceItem,
  ProductStockItem,
  VariantTemplate,
  AddonTemplate,
  StockAlertItem,
  Client,
  RegistrationRequest,
  OptionSet,
  OptionSetItem,
  OrderDetail,
  OrderStatus,
  OrderSummary,
  SubmitOrderPayload,
  SubmitOrderResult,
  TenantContext,
  TenantSettings,
  VisitingCardConfig,
  VisitingCardPayload,
  Promotion,
  PromotionProductItem,
  Announcement,
  AnnouncementPublishMode,
  PhotographerGallery,
  PhotographerGallerySettings,
  GalleryBotCodes,
  GalleryPhoto,
  GallerySection,
  GalleryLayout,
  GallerySort,
  GalleryTitleFont,
  GalleryWatermark,
  GalleryWatermarkPlace,
  GalleryPlanFeatures,
  PortfolioItem,
  PortfolioSettings,
  TenantNote,
  NoteColorKey,
  NoteSiteBlock,
  NoteBlogLayout,
  NoteImageFit,
  NoteImageWidth,
  NoteAltStart,
  NoteAltTextAlign,
  NoteColorRole,
  PublicBlogNote,
  PublicBlogPost,
  PublicBlogPostSummary,
  I18nBlock,
  PlatformBackupFile,
  PlatformBackupStatus,
  LoginDestination,
  PlatformJoinRequest,
  ReferralApplication,
  ReferralPartner,
  ReferralConversion,
  ReferralPayoutRequest,
  PartnerCabinetResponse,
  ReferralPayoutDetails,
  ExtensionCatalogItem,
  ExtensionAvailability,
  ExtensionOrder,
  PlatformExtensionCatalogEntry,
  PublisherDraft,
  PublisherSourceListItem,
  PublisherSourceType,
  PlatformSeoAuditReport,
  PlatformStaffMember,
  TenantOverviewMetrics,
  TenantStaffMember,
  TenantStaffRole,
  TenantModuleKey,
  PublicTenantListing,
  Language,
  ImportMode,
  ImportPreviewResult,
  ImportApplyResult,
  TelegramLinkStatus,
  TelegramLinkTokenResponse,
  GlobalBook,
  GlobalBookPhoto,
  GlobalBookWrite,
  PublicGlobalBook,
} from '../types';

const TOKEN_KEY = 'catalogofworks_token';

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

function authHeaders(): HeadersInit {
  const token = getStoredToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Auth + tenant scope for multipart uploads (no Content-Type — browser sets boundary). */
function tenantUploadHeaders(): HeadersInit {
  const token = getStoredToken();
  return {
    ...tenantScopeHeaders(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function readPreviewLink(res: Response) {
  if (!res.ok) throw new Error('preview_failed');
  const data = await res.json().catch(() => null) as { url?: unknown } | null;
  if (!data || typeof data.url !== 'string' || !data.url) throw new Error('preview_failed');
  return data.url;
}

/** Platform multipart uploads — Authorization only (never force application/json). */
function platformUploadHeaders(): HeadersInit {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Auth + X-Tenant-Slug for /api/public/* on catalogofworks.com?tenant=slug (apex dev/prod). */
function storefrontHeaders(): HeadersInit {
  return { ...authHeaders(), ...tenantScopeHeaders() };
}

async function parseJson(res: Response) {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

type WallPaletteColor = { l: number; a: number; b: number; w: number };

function readStyleBands(data: { styles?: unknown; moreStyles?: unknown }) {
  const read = (rows: unknown) => (
    Array.isArray(rows)
      ? rows
        .map((row: { id?: unknown; label?: unknown }) => ({
          id: String(row?.id || ''),
          label: String(row?.label || '').trim(),
        }))
        .filter((row) => row.id && row.label)
      : []
  );
  return { styles: read(data.styles), moreStyles: read(data.moreStyles) };
}

function readWallWorks(data: { works?: unknown }) {
  return Array.isArray(data.works)
    ? data.works
      .map((row: { id?: unknown; slug?: unknown; title?: unknown; author?: unknown; imageUrl?: unknown }) => ({
        id: String(row?.id || ''),
        slug: String(row?.slug || ''),
        title: String(row?.title || '').trim(),
        author: String(row?.author || '').trim(),
        imageUrl: typeof row?.imageUrl === 'string' && row.imageUrl.trim() ? row.imageUrl.trim() : null,
      }))
      .filter((row) => row.id)
    : [];
}

type PlatformSliderApiItem = {
  tenantSlug: string;
  productId: string;
  imageUrl: string;
};

function tenantSlugFromUploadPath(imageUrl: string): string {
  const match = String(imageUrl).match(/^\/uploads\/([^/]+)\//);
  return match?.[1] ?? '';
}

/** Accept both cached `items` and legacy `imageUrls` API shapes. */
function normalizePlatformSliderItems(data: Record<string, unknown>): PlatformSliderApiItem[] {
  if (Array.isArray(data.items) && data.items.length > 0) {
    return data.items
      .map((raw) => {
        if (!raw || typeof raw !== 'object') {
          return null;
        }
        const item = raw as Record<string, unknown>;
        const imageUrl = typeof item.imageUrl === 'string' ? item.imageUrl : '';
        if (!imageUrl) {
          return null;
        }
        return {
          tenantSlug: typeof item.tenantSlug === 'string' ? item.tenantSlug : '',
          productId: typeof item.productId === 'string' ? item.productId : String(item.productId ?? ''),
          imageUrl,
        };
      })
      .filter((item): item is PlatformSliderApiItem => Boolean(item));
  }

  if (!Array.isArray(data.imageUrls)) {
    return [];
  }

  return data.imageUrls
    .map((url) => {
      const imageUrl = typeof url === 'string' ? url : '';
      if (!imageUrl) {
        return null;
      }
      return {
        tenantSlug: tenantSlugFromUploadPath(imageUrl),
        productId: '',
        imageUrl,
      };
    })
    .filter((item): item is PlatformSliderApiItem => Boolean(item));
}

async function readArchiveFiles(res: Response) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !Array.isArray(data.files)) {
    throw new Error(data.error || 'download_failed');
  }
  return data.files as { name: string; url: string }[];
}

function putSignedFile(
  url: string,
  headers: Record<string, string>,
  file: File,
  onProgress: ((loaded: number, total: number) => void) | undefined,
  errorCode: string,
  signal?: AbortSignal,
) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('upload_aborted'));
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort);
    const finish = (settle: () => void) => {
      signal?.removeEventListener('abort', onAbort);
      settle();
    };
    xhr.open('PUT', url);
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.upload.onprogress = (event) => {
      if (signal?.aborted || !event.lengthComputable || !onProgress) return;
      onProgress(event.loaded, event.lengthComputable ? event.total : file.size);
    };
    xhr.onload = () => finish(() => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(errorCode));
    });
    xhr.onerror = () => finish(() => reject(new Error(errorCode)));
    xhr.onabort = () => finish(() => reject(new Error('upload_aborted')));
    xhr.send(file);
  });
}

const HOLYLAND_ARCHIVE_LIMIT = 512 * 1024 * 1024;
const HOLYLAND_CHUNK_BYTES = 8 * 1024 * 1024;

type HolylandArchiveStep = {
  step: string;
  loaded?: number;
  total?: number;
  done?: number;
  error?: string;
  preview?: ImportPreviewResult;
  result?: ImportApplyResult;
};

function postHolylandChunk(
  uploadId: string,
  index: number,
  total: number,
  totalBytes: number,
  blob: Blob,
  onLoaded: (loaded: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/tenant/import/archive/chunk');
    const headers = tenantUploadHeaders() as Record<string, string>;
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, String(value));
    });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onLoaded(Math.min(event.loaded, blob.size));
    };
    xhr.onload = () => {
      let data: { error?: string } = {};
      try {
        data = JSON.parse(xhr.responseText || '{}') as { error?: string };
      } catch {
        data = {};
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(Object.assign(new Error(data.error || 'upload_failed'), { data, status: xhr.status }));
        return;
      }
      resolve();
    };
    xhr.onerror = () => {
      reject(Object.assign(new Error('upload_failed'), { data: { error: 'upload_failed' } }));
    };
    const body = new FormData();
    body.append('uploadId', uploadId);
    body.append('index', String(index));
    body.append('total', String(total));
    body.append('totalBytes', String(totalBytes));
    body.append('file', blob, 'chunk');
    xhr.send(body);
  });
}

async function readHolylandArchiveStream(
  path: string,
  init: RequestInit,
  onStep?: (step: HolylandArchiveStep) => void,
): Promise<HolylandArchiveStep | null> {
  const res = await fetch(`/api/tenant${path}`, init);
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('ndjson')) {
    const data = await res.json().catch(() => ({})) as { error?: string; preview?: ImportPreviewResult };
    throw Object.assign(new Error(data.error || 'import_preview_failed'), { data, status: res.status });
  }
  const reader = res.body?.getReader();
  if (!reader) {
    throw Object.assign(new Error('import_preview_failed'), { data: { error: 'import_preview_failed' } });
  }
  const decoder = new TextDecoder();
  let buf = '';
  let last: HolylandArchiveStep | null = null;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop() || '';
    for (const line of lines) {
      if (!line.trim()) continue;
      const row = JSON.parse(line) as HolylandArchiveStep;
      if (row.step === 'error') {
        throw Object.assign(new Error(row.error || 'import_preview_failed'), {
          data: { error: row.error, preview: row.preview },
        });
      }
      onStep?.(row);
      if (row.step === 'done') last = row;
    }
  }
  return last;
}

async function runHolylandArchiveJob(
  path: string,
  file: File,
  extra: Record<string, unknown>,
  onStep?: (step: HolylandArchiveStep) => void,
) {
  if (file.size > HOLYLAND_ARCHIVE_LIMIT) {
    throw Object.assign(new Error('zip_too_large'), { data: { error: 'zip_too_large' } });
  }
  const uploadId = crypto.randomUUID();
  const total = Math.max(1, Math.ceil(file.size / HOLYLAND_CHUNK_BYTES));
  let sent = 0;
  for (let index = 0; index < total; index += 1) {
    const start = index * HOLYLAND_CHUNK_BYTES;
    const blob = file.slice(start, start + HOLYLAND_CHUNK_BYTES);
    await postHolylandChunk(uploadId, index, total, file.size, blob, (loaded) => {
      onStep?.({
        step: 'upload',
        loaded: Math.min(file.size, sent + loaded),
        total: file.size,
      });
    });
    sent += blob.size;
    onStep?.({ step: 'upload', loaded: sent, total: file.size });
  }
  onStep?.({ step: 'assemble' });
  return readHolylandArchiveStream(path, {
    method: 'POST',
    headers: { ...authHeaders(), ...tenantScopeHeaders() },
    body: JSON.stringify({
      uploadId,
      total,
      totalBytes: file.size,
      ...extra,
    }),
  }, onStep);
}

export type ClientAccountProfile = {
  email: string;
  fullName: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country: string;
  phone: string;
  discountPercent: number | null;
};

export const api = {
  async login(email: string, password?: string, tenantSlug?: string | null) {
    const qs = tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
    const res = await fetch(`/api/auth/login${qs}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password: password || '' }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'login_failed'), { data, status: res.status });
    }
    if (data.chooseDestination) {
      return data as {
        chooseDestination: true;
        pickToken: string;
        destinations: LoginDestination[];
      };
    }
    setStoredToken(data.token);
    return data as { token: string; session: AuthSession };
  },

  async completeLoginDestination(pickToken: string, destination: string) {
    const res = await fetch('/api/auth/login/destination', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pickToken, destination }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'login_destination_failed'), { data, status: res.status });
    }
    setStoredToken(data.token);
    return data as { token: string; session: AuthSession };
  },

  async requestLoginOtp(email: string, tenantSlug?: string | null, lang?: Language) {
    const qs = tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
    const res = await fetch(`/api/auth/otp/request${qs}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, lang }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'otp_request_failed'), { data, status: res.status });
    }
    return data as { ok: true; sent: true };
  },

  async verifyLoginOtp(email: string, code: string, tenantSlug?: string | null) {
    const qs = tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
    const res = await fetch(`/api/auth/otp/verify${qs}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'otp_verify_failed'), { data, status: res.status });
    }
    if (data.chooseDestination) {
      return data as {
        chooseDestination: true;
        pickToken: string;
        destinations: LoginDestination[];
      };
    }
    setStoredToken(data.token);
    return data as { token: string; session: AuthSession };
  },

  /** @deprecated Use login() — single entry point for all roles */
  async tenantLogin(email: string, password?: string, tenantSlug?: string | null) {
    return this.login(email, password, tenantSlug);
  },

  /** @deprecated Use login() — platform admins use the same /login page */
  async platformLogin(email: string, password: string) {
    return this.login(email, password);
  },

  async fetchSession() {
    const res = await fetch('/api/auth/me', { headers: authHeaders() });
    if (res.status === 401) {
      setStoredToken(null);
      return null;
    }
    const data = await parseJson(res);
    if (!res.ok) {
      return null;
    }
    return data.session as AuthSession;
  },

  async logout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST', headers: authHeaders() });
    } finally {
      setStoredToken(null);
    }
  },

  async changePlatformPassword(body: { currentPassword: string; newPassword: string }) {
    return api.platformRequest('/change-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ ok: true }>;
  },

  async submitRegistrationRequest(body: {
    email?: string;
    name: string;
    phone?: string;
    message?: string;
    kind?: 'registration' | 'message' | 'order_cancel' | 'a11y_feedback';
  }) {
    const res = await fetch('/api/auth/registration-request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw new Error(data.error || 'registration_failed');
    }
    return data;
  },

  async fetchTenantContext(tenantSlug?: string | null, devFallback = false) {
    const params = new URLSearchParams();
    if (tenantSlug) {
      params.set('tenant', tenantSlug);
    }
    if (devFallback) {
      params.set('devFallback', '1');
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/api/public/tenant-context${qs}`);
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'tenant_failed'), { data, status: res.status });
    }
    return data.tenant;
  },

  async fetchTelegramCatalogTenants(lang?: Language) {
    const params = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    const res = await fetch(`/api/public/telegram/catalog-tenants${params}`);
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_tenants_failed'), { data, status: res.status });
    }
    return data as {
      tenants: PublicTenantListing[];
      catalogBotUsername: string;
      homeUrl: string;
    };
  },

  async fetchTelegramCatalogMeta(tenantSlug: string) {
    const params = new URLSearchParams({ tenant: tenantSlug });
    const res = await fetch(`/api/public/telegram/catalog-meta?${params.toString()}`, {
      headers: { ...tenantScopeHeaders(tenantSlug) },
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'telegram_meta_failed'), { data, status: res.status });
    }
    return data as {
      slug: string;
      name: string;
      catalogEnabled: boolean;
      miniAppUrl: string | null;
      policies: TenantContext['policies'];
    };
  },

  async verifyTelegramInitData(tenantSlug: string, initData: string) {
    const res = await fetch('/api/public/telegram/verify-init', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...tenantScopeHeaders(tenantSlug) },
      body: JSON.stringify({ initData }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'telegram_verify_failed'), { data, status: res.status });
    }
    return data as { ok: boolean; telegramUserId: number | null; catalogEnabled: boolean };
  },

  async linkTelegramClient(tenantSlug: string, initData: string, lang?: string) {
    const res = await fetch('/api/public/telegram/link-client', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders(), ...tenantScopeHeaders(tenantSlug) },
      body: JSON.stringify({ initData, lang }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'telegram_link_failed'), { data, status: res.status });
    }
    return data as { ok: boolean; linked: boolean };
  },

  async fetchPublicTenants(lang?: Language) {
    const query = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    const res = await fetch(`/api/public/tenants${query}`);
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'tenants_directory_failed'), { data, status: res.status });
    }
    return {
      tenants: (data.tenants ?? []) as PublicTenantListing[],
    };
  },

  async fetchPublicCatalogSearch(lang: Language, query: string) {
    const params = new URLSearchParams({ lang });
    const q = query.trim();
    if (q) params.set('q', q);
    const res = await fetch(`/api/public/catalog-search?${params.toString()}`);
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_search_failed'), { data, status: res.status });
    }
    return {
      ...readStyleBands(data),
      works: readWallWorks(data),
    };
  },

  async matchPublicCatalog(
    lang: Language,
    body: { palette: WallPaletteColor[]; styleId?: string | null; principle?: string; accent?: WallPaletteColor | null },
  ) {
    const res = await fetch('/api/public/catalog-search/match', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, lang }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_search_failed'), { data, status: res.status });
    }
    return { works: readWallWorks(data) };
  },

  async fetchStorefrontStyleBands() {
    const res = await fetch('/api/public/catalog/style-bands', { headers: storefrontHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_popular_terms_failed'), { data, status: res.status });
    }
    return readStyleBands(data);
  },

  async matchStorefrontCatalog(
    lang: Language,
    body: { palette: WallPaletteColor[]; styleId?: string | null; principle?: string; accent?: WallPaletteColor | null },
  ) {
    const res = await fetch('/api/public/catalog/match', {
      method: 'POST',
      headers: { ...storefrontHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, lang }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_search_failed'), { data, status: res.status });
    }
    return { works: readWallWorks(data) };
  },

  async fetchPlatformSliderImages(lang?: Language) {
    const query = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    const res = await fetch(`/api/public/platform-slider-images${query}`);
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'platform_slider_images_failed'), {
        data,
        status: res.status,
      });
    }
    const items = normalizePlatformSliderItems(data);
    return {
      items,
      refreshedAt: typeof data.refreshedAt === 'string' ? data.refreshedAt : null,
    };
  },

  getPublicPlanPricing() {
    return fetch('/api/public/plan-pricing').then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw Object.assign(new Error(data.error || 'plan_pricing_failed'), { data, status: res.status });
      }
      return data as { ilsPerUsd: number; asOf: string | null; country: string | null };
    });
  },

  getPublicPlanCatalog() {
    return fetch('/api/public/plan-catalog').then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw Object.assign(new Error(data.error || 'plan_catalog_failed'), { data, status: res.status });
      }
      return data as {
        plans: Array<{
          id: string;
          sortOrder: number;
          storageGb: number;
          priceMonthly: number;
          features: Record<string, boolean>;
        }>;
        featureKeys: string[];
      };
    });
  },

  requestLegalAccountDeletion(body: { name: string; email: string; message: string; lang: string }) {
    return fetch('/api/public/account-deletion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw Object.assign(new Error(data.error || 'deletion_failed'), { data, status: res.status });
      }
      return data as { ok: true };
    });
  },

  previewSignupSlug(body: { email?: string; slug?: string }) {
    return fetch('/api/public/free-signup/slug', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      return {
        ok: res.ok === true,
        error: typeof data.error === 'string' ? data.error : '',
        slug: typeof data.slug === 'string' ? data.slug : '',
        host: typeof data.host === 'string' ? data.host : '',
        suffix: typeof data.suffix === 'string' ? data.suffix : '',
      };
    });
  },

  requestFreeSignup(body: { name: string; email: string; slug: string; lang: string }) {
    return fetch('/api/public/free-signup/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw Object.assign(new Error(data.error || 'signup_failed'), { data, status: res.status });
      }
      return data as { ok: true; sent: true };
    });
  },

  createTrip(body: { email: string; albumName: string; days: number; name: string; lang: string; agreed: boolean }) {
    return fetch('/api/public/trip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw Object.assign(new Error(data.error || 'trip_failed'), { data, status: res.status });
      }
      return data as { ok: true; sent: true };
    });
  },

  confirmFreeSignup(body: { email: string; code: string; lang: string }) {
    return fetch('/api/public/free-signup/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw Object.assign(new Error(data.error || 'signup_failed'), { data, status: res.status });
      }
      return data as { ok: true; slug: string; enterUrl: string };
    });
  },

  async submitPlatformJoinRequest(body: {
    companyName: string;
    contactName: string;
    email: string;
    phone?: string;
    message?: string;
    lang?: string;
  }) {
    const res = await fetch('/api/public/platform-join-request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        companyName: body.companyName,
        contactName: body.contactName,
        email: body.email,
        phone: body.phone || '',
        message: body.message || '',
        lang: body.lang || '',
      }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'platform_join_request_failed'), { data, status: res.status });
    }
    return data as { ok: boolean; request: unknown; referralDiscountLabel?: string | null };
  },

  async submitReferralApplication(body: {
    contactName: string;
    telegram?: string;
    email?: string;
    phone?: string;
    locality: 'local' | 'foreign';
    message?: string;
  }) {
    const res = await fetch('/api/public/referral-application', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contactName: body.contactName,
        telegram: body.telegram || '',
        email: body.email || '',
        phone: body.phone || '',
        locality: body.locality,
        message: body.message || '',
      }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'referral_application_failed'), { data, status: res.status });
    }
    return data;
  },

  async fetchCatalogCategories() {
    const res = await fetch('/api/public/catalog/categories', { headers: storefrontHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_categories_failed'), { data, status: res.status });
    }
    return data as { categories: Category[] };
  },

  async fetchCatalogPopularTerms() {
    const res = await fetch('/api/public/catalog/popular-terms', { headers: storefrontHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_popular_terms_failed'), { data, status: res.status });
    }
    return data as { terms: Array<{ id: string; label: string }> };
  },

  async fetchEditorialCollection() {
    const res = await fetch('/api/public/catalog/editorial-collection', {
      headers: storefrontHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'editorial_collection_failed'), {
        data,
        status: res.status,
      });
    }
    return data as EditorialCollectionPayload;
  },

  async fetchCatalogPromotions() {
    const res = await fetch('/api/public/catalog/promotions', { headers: storefrontHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_promotions_failed'), { data, status: res.status });
    }
    return data as { promotions: CatalogPromotionSummary[] };
  },

  async fetchCatalogProducts(params?: {
    search?: string;
    categoryId?: string;
    promotionId?: string;
    lowStock?: boolean;
    attributeFilters?: CatalogAttributeFilters;
    optionSetFilters?: Array<{ setKey: string; itemKeys: string[] }>;
    ids?: string[];
    page?: number;
    limit?: number;
    sort?: 'newest';
    landingContext?: boolean;
    facts?: boolean;
  }) {
    const qs = new URLSearchParams();
    if (params?.facts) qs.set('facts', '1');
    if (params?.search?.trim()) qs.set('search', params.search.trim());
    if (params?.categoryId) qs.set('categoryId', params.categoryId);
    if (params?.promotionId) qs.set('promotionId', params.promotionId);
    if (params?.lowStock) qs.set('lowStock', '1');
    if (params?.attributeFilters && Object.keys(params.attributeFilters).length > 0) {
      qs.set('attributeFilters', JSON.stringify(params.attributeFilters));
    }
    if (params?.optionSetFilters?.length) {
      qs.set('optionSetFilters', JSON.stringify(params.optionSetFilters));
    }
    if (params?.ids?.length) qs.set('ids', params.ids.join(','));
    if (params?.page != null) qs.set('page', String(params.page));
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.sort === 'newest') qs.set('sort', 'newest');
    if (params?.landingContext) qs.set('context', 'landing');
    const suffix = qs.toString() ? `?${qs}` : '';
    const res = await fetch(`/api/public/catalog/products${suffix}`, { headers: storefrontHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_products_failed'), { data, status: res.status });
    }
    return data as CatalogProductsPage;
  },

  async requestClientRegistration(body: {
    name: string;
    email: string;
    address?: string;
    phone?: string;
    newsletter: boolean;
    lang: string;
  }) {
    const res = await fetch('/api/public/register/request', {
      method: 'POST',
      headers: storefrontHeaders(),
      body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'register_failed'), { data, status: res.status });
    }
    return data as { ok: true };
  },

  async requestClientLogin(body: { email: string; lang: string }) {
    const res = await fetch('/api/public/login/request', {
      method: 'POST',
      headers: storefrontHeaders(),
      body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'login_failed'), { data, status: res.status });
    }
    return data as { ok: true };
  },

  async confirmClientLogin(body: { email: string; code: string }) {
    const res = await fetch('/api/public/login/confirm', {
      method: 'POST',
      headers: storefrontHeaders(),
      body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'login_confirm_failed'), { data, status: res.status });
    }
    return data as { token: string; session: { type: 'client'; id: string; email: string; name: string } };
  },

  async fetchClientAccount() {
    const res = await fetch('/api/public/account', { headers: storefrontHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'account_failed'), { data, status: res.status });
    }
    return data as ClientAccountProfile;
  },

  async saveClientAccount(body: {
    fullName: string;
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    country: string;
    phone: string;
  }) {
    const res = await fetch('/api/public/account', {
      method: 'PUT',
      headers: storefrontHeaders(),
      body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'account_failed'), { data, status: res.status });
    }
    return data as ClientAccountProfile;
  },

  async requestAccountDeletion(body: { reason: string; lang: string }) {
    const res = await fetch('/api/public/account/delete/request', {
      method: 'POST',
      headers: storefrontHeaders(),
      body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'account_delete_failed'), { data, status: res.status });
    }
    return data as { ok: true };
  },

  async confirmAccountDeletion(body: { code: string }) {
    const res = await fetch('/api/public/account/delete/confirm', {
      method: 'POST',
      headers: storefrontHeaders(),
      body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'account_delete_failed'), { data, status: res.status });
    }
    return data as { ok: true };
  },

  async confirmClientRegistration(body: { email: string; code: string }) {
    const res = await fetch('/api/public/register/confirm', {
      method: 'POST',
      headers: storefrontHeaders(),
      body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'register_confirm_failed'), { data, status: res.status });
    }
    return data as { token: string; session: { type: 'client'; id: string; email: string; name: string } };
  },

  async newsletterStatus(email: string) {
    const res = await fetch('/api/public/newsletter/status', {
      method: 'POST',
      headers: storefrontHeaders(),
      body: JSON.stringify({ email }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'newsletter_status_failed'), { data, status: res.status });
    }
    return data as { subscribed: boolean };
  },

  async newsletterSubscribe(body: {
    email: string;
    consentTerms: boolean;
    consentTracking: boolean;
    lang: string;
  }) {
    const res = await fetch('/api/public/newsletter/subscribe', {
      method: 'POST',
      headers: storefrontHeaders(),
      body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'newsletter_subscribe_failed'), { data, status: res.status });
    }
    return data as { subscribed: true; mailed: boolean };
  },

  async newsletterUnsubscribe(email: string, lang: string) {
    const res = await fetch('/api/public/newsletter/unsubscribe', {
      method: 'POST',
      headers: storefrontHeaders(),
      body: JSON.stringify({ email, lang }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'newsletter_unsubscribe_failed'), { data, status: res.status });
    }
    return data as { subscribed: false; mailed: boolean };
  },

  async fetchShopExchangeRates() {
    const res = await fetch('/api/public/catalog/exchange-rates', { headers: storefrontHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'exchange_rates_failed'), { data, status: res.status });
    }
    return data as {
      base: 'ILS';
      ilsPerUsd: number | null;
      ilsPerEur: number | null;
      ilsPerRub: number | null;
      ilsPerUah: number | null;
      ilsPerGel: number | null;
      asOf: string | null;
    };
  },

  async getPublicProductsByIds(ids: string[], lang: Language = 'ru') {
    const unique = [...new Set(ids.map((id) => String(id || '').trim()).filter(Boolean))].slice(0, 48);
    if (!unique.length) {
      return { products: [] as Array<{
        id: string;
        name: string;
        description: string;
        imageUrl: string | null;
        externalUrl: string | null;
        price: number | null;
      }> };
    }
    const qs = new URLSearchParams({
      ids: unique.join(','),
      lang,
    });
    const res = await fetch(`/api/public/catalog/products-by-ids?${qs}`, {
      headers: storefrontHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_products_by_ids_failed'), {
        data,
        status: res.status,
      });
    }
    return data as {
      products: Array<{
        id: string;
        name: string;
        description: string;
        imageUrl: string | null;
        externalUrl: string | null;
        price: number | null;
      }>;
    };
  },

  async fetchCatalogFilters(params?: {
    search?: string;
    categoryId?: string;
    promotionId?: string;
    lowStock?: boolean;
    attributeFilters?: CatalogAttributeFilters;
  }) {
    const qs = new URLSearchParams();
    if (params?.search?.trim()) qs.set('search', params.search.trim());
    if (params?.categoryId) qs.set('categoryId', params.categoryId);
    if (params?.promotionId) qs.set('promotionId', params.promotionId);
    if (params?.lowStock) qs.set('lowStock', '1');
    if (params?.attributeFilters && Object.keys(params.attributeFilters).length > 0) {
      qs.set('attributeFilters', JSON.stringify(params.attributeFilters));
    }
    const suffix = qs.toString() ? `?${qs}` : '';
    const res = await fetch(`/api/public/catalog/filters${suffix}`, { headers: storefrontHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_filters_failed'), { data, status: res.status });
    }
    return data as { attributes: CatalogFilterAttribute[] };
  },

  /**
   * Public catalog variant tag cloud (indexed from resolved product/template configs).
   */
  async fetchCatalogVariantTags(params?: { categoryId?: string }) {
    const qs = new URLSearchParams();
    if (params?.categoryId) qs.set('categoryId', params.categoryId);
    const suffix = qs.toString() ? `?${qs}` : '';
    const res = await fetch(`/api/public/catalog/variant-tags${suffix}`, {
      headers: storefrontHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_variant_tags_failed'), {
        data,
        status: res.status,
      });
    }
    return data as {
      tags: Array<{
        axisKey: string;
        valueKey: string;
        axisLabelI18n: I18nBlock;
        valueLabelI18n: I18nBlock;
        productCount: number;
      }>;
    };
  },

  /**
   * Public catalog option-set facets (named dictionaries attached to products).
   */
  async fetchCatalogOptionSetFacets(params?: { setKeys?: string[]; categoryId?: string }) {
    const qs = new URLSearchParams();
    if (params?.setKeys?.length) qs.set('setKeys', params.setKeys.join(','));
    if (params?.categoryId) qs.set('categoryId', params.categoryId);
    const suffix = qs.toString() ? `?${qs}` : '';
    const res = await fetch(`/api/public/catalog/option-set-facets${suffix}`, {
      headers: storefrontHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_option_set_facets_failed'), {
        data,
        status: res.status,
      });
    }
    return data as { optionSets: OptionSet[] };
  },

  async fetchCatalogProduct(id: string) {
    const res = await fetch(`/api/public/catalog/products/${encodeURIComponent(id)}`, {
      headers: storefrontHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'catalog_product_failed'), { data, status: res.status });
    }
    return data as { product: CatalogProductDetail };
  },

  async submitOrder(payload: SubmitOrderPayload) {
    const res = await fetch('/api/public/orders', {
      method: 'POST',
      headers: storefrontHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'order_submit_failed'), { data, status: res.status });
    }
    return data as SubmitOrderResult;
  },

  async listClientOrders() {
    const res = await fetch('/api/public/orders', { headers: storefrontHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'orders_list_failed'), { data, status: res.status });
    }
    return data as { orders: OrderSummary[] };
  },

  async fetchClientOrder(id: string) {
    const res = await fetch(`/api/public/orders/${encodeURIComponent(id)}`, { headers: storefrontHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'orders_load_failed'), { data, status: res.status });
    }
    return data as { order: OrderDetail };
  },

  async listClientFavorites() {
    const res = await fetch('/api/public/favorites', { headers: storefrontHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'favorites_list_failed'), { data, status: res.status });
    }
    return data as { productIds: string[] };
  },

  async mergeClientFavorites(productIds: string[]) {
    const res = await fetch('/api/public/favorites/merge', {
      method: 'POST',
      headers: storefrontHeaders(),
      body: JSON.stringify({ productIds }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'favorites_merge_failed'), { data, status: res.status });
    }
    return data as { merged: number; productIds: string[] };
  },

  async addClientFavorite(productId: string) {
    const res = await fetch(`/api/public/favorites/${encodeURIComponent(productId)}`, {
      method: 'POST',
      headers: storefrontHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'favorites_add_failed'), { data, status: res.status });
    }
    return data as { ok: true };
  },

  async removeClientFavorite(productId: string) {
    const res = await fetch(`/api/public/favorites/${encodeURIComponent(productId)}`, {
      method: 'DELETE',
      headers: storefrontHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'favorites_remove_failed'), { data, status: res.status });
    }
    return data as { ok: true };
  },

  async listPublicAnnouncements(tenantSlug?: string | null) {
    const res = await fetch('/api/public/announcements', {
      headers: {
        ...storefrontHeaders(),
        ...tenantScopeHeaders(tenantSlug),
      },
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'announcements_list_failed'), { data, status: res.status });
    }
    return data as { announcements: Announcement[] };
  },

  listTenantAnnouncements() {
    return api.tenantRequest('/announcements') as Promise<{
      announcements: Announcement[];
      announcementCount?: number;
      maxAnnouncements?: number;
    }>;
  },

  getTenantAnnouncement(id: string) {
    return api.tenantRequest(`/announcements/${encodeURIComponent(id)}`) as Promise<{
      announcement: Announcement;
    }>;
  },

  createTenantAnnouncement(body: {
    titleI18n: Record<string, string>;
    contentMdI18n: Record<string, string>;
    contentMd?: string;
    imageUrl?: string | null;
    publishMode: AnnouncementPublishMode;
    scheduledAt?: string | null;
    /** After tenant confirms: delete oldest sent to free a slot. */
    evictOldest?: boolean;
  }) {
    return api.tenantRequest('/announcements', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ announcement: Announcement }>;
  },

  updateTenantAnnouncement(
    id: string,
    body: {
      titleI18n: Record<string, string>;
      contentMdI18n: Record<string, string>;
      contentMd?: string;
      imageUrl?: string | null;
      publishMode: AnnouncementPublishMode;
      scheduledAt?: string | null;
      evictOldest?: boolean;
    },
  ) {
    return api.tenantRequest(`/announcements/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ announcement: Announcement }>;
  },

  publishTenantAnnouncement(id: string, body?: { evictOldest?: boolean }) {
    return api.tenantRequest(`/announcements/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }) as Promise<{ announcement: Announcement }>;
  },

  deleteTenantAnnouncement(id: string) {
    return api.tenantRequest(`/announcements/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }) as Promise<{ ok: true }>;
  },

  listTenantNotes(filters?: { search?: string; tag?: string }) {
    const params = new URLSearchParams();
    if (filters?.search) params.set('search', filters.search);
    if (filters?.tag) params.set('tag', filters.tag);
    const qs = params.toString();
    return api.tenantRequest(`/notes${qs ? `?${qs}` : ''}`) as Promise<{ notes: TenantNote[] }>;
  },

  getTenantNote(id: string) {
    return api.tenantRequest(`/notes/${encodeURIComponent(id)}`) as Promise<{ note: TenantNote }>;
  },

  createTenantNote(body: {
    title?: string | null;
    bodyMd?: string;
    titleI18n?: I18nBlock;
    bodyMdI18n?: I18nBlock;
    colorKey?: NoteColorKey;
    tags?: string[];
    imageUrls?: string[];
    siteBlock?: NoteSiteBlock;
    blogVisible?: boolean;
    blogLayout?: NoteBlogLayout;
    imageFit?: NoteImageFit;
    imageWidth?: NoteImageWidth;
    altGap?: number;
    altPadding?: number;
    altTextInset?: number;
    altTextAlignA?: NoteAltTextAlign;
    altTextAlignB?: NoteAltTextAlign;
    altStart?: NoteAltStart;
    altBgA?: NoteColorRole;
    altTextA?: NoteColorRole;
    altBgB?: NoteColorRole;
    altTextB?: NoteColorRole;
    deletionLocked?: boolean;
  }) {
    return api.tenantRequest('/notes', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ note: TenantNote }>;
  },

  updateTenantNote(
    id: string,
    body: {
      title?: string | null;
      bodyMd?: string;
      titleI18n?: I18nBlock;
      bodyMdI18n?: I18nBlock;
      colorKey?: NoteColorKey;
      tags?: string[];
      imageUrls?: string[];
      siteBlock?: NoteSiteBlock;
      blogVisible?: boolean;
      blogLayout?: NoteBlogLayout;
      imageFit?: NoteImageFit;
      imageWidth?: NoteImageWidth;
      altGap?: number;
      altPadding?: number;
      altTextInset?: number;
      altTextAlignA?: NoteAltTextAlign;
      altTextAlignB?: NoteAltTextAlign;
      altStart?: NoteAltStart;
      altBgA?: NoteColorRole;
      altTextA?: NoteColorRole;
      altBgB?: NoteColorRole;
      altTextB?: NoteColorRole;
      deletionLocked?: boolean;
    },
  ) {
    return api.tenantRequest(`/notes/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ note: TenantNote }>;
  },

  deleteTenantNote(id: string) {
    return api.tenantRequest(`/notes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }) as Promise<{ ok: true }>;
  },

  async listPublicBlogNotes(block: NoteSiteBlock | string = 'block1') {
    const params = new URLSearchParams();
    if (block && block !== 'none') params.set('block', String(block));
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/api/public/blog-notes${qs}`, {
      headers: storefrontHeaders(),
      cache: 'no-store',
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'blog_notes_failed'), { data, status: res.status });
    }
    return data as { notes: PublicBlogNote[]; block?: string };
  },

  async listPublicBlog(lang: Language) {
    const res = await fetch(`/api/public/blog?lang=${encodeURIComponent(lang)}`, {
      headers: storefrontHeaders(),
      cache: 'no-store',
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data?.error || 'blog_list_failed'), { status: res.status, data });
    }
    return data as { posts: PublicBlogPostSummary[]; lang: Language };
  },

  /** null when the post is missing or not written in this language. */
  async getPublicBlogPost(key: string, lang: Language) {
    const res = await fetch(`/api/public/blog/${encodeURIComponent(key)}?lang=${encodeURIComponent(lang)}`, {
      headers: storefrontHeaders(),
      cache: 'no-store',
    });
    if (res.status === 404) return null;
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data?.error || 'blog_post_failed'), { status: res.status, data });
    }
    return (data as { post: PublicBlogPost }).post;
  },

  async listPublicNoteByContentTag(tag: string) {
    const params = new URLSearchParams();
    params.set('tag', String(tag || '').trim().toLowerCase());
    const res = await fetch(`/api/public/blog-notes?${params.toString()}`, {
      headers: storefrontHeaders(),
      cache: 'no-store',
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'blog_notes_failed'), { data, status: res.status });
    }
    return data as { notes: PublicBlogNote[]; tag?: string };
  },

  async listOccupiedContentTags() {
    const res = await fetch('/api/public/blog-notes/occupied', {
      headers: storefrontHeaders(),
      cache: 'no-store',
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'blog_notes_occupied_failed'), {
        data,
        status: res.status,
      });
    }
    return data as { tags: string[]; occupied?: NoteSiteBlock[] };
  },

  async listOccupiedSiteBlocks() {
    const res = await api.listOccupiedContentTags();
    return { occupied: res.occupied ?? [] };
  },

  listOptionSets(params?: { includeInactive?: boolean }) {
    const suffix = params?.includeInactive ? '?includeInactive=1' : '';
    return api.tenantRequest(`/option-sets${suffix}`) as Promise<{ optionSets: OptionSet[] }>;
  },

  createOptionSet(body: {
    key: string;
    nameI18n: Record<string, string>;
    filterTitleI18n?: Record<string, string>;
    sortOrder?: number;
    active?: boolean;
  }) {
    return api.tenantRequest('/option-sets', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ optionSet: OptionSet }>;
  },

  updateOptionSet(
    id: string,
    body: Partial<{
      nameI18n: Record<string, string>;
      filterTitleI18n: Record<string, string>;
      sortOrder: number;
      active: boolean;
    }>,
  ) {
    return api.tenantRequest(`/option-sets/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ optionSet: OptionSet }>;
  },

  createOptionSetItem(
    setId: string,
    body: {
      key: string;
      labelI18n: Record<string, string>;
      sortOrder?: number;
      active?: boolean;
    },
  ) {
    return api.tenantRequest(`/option-sets/${encodeURIComponent(setId)}/items`, {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ item: OptionSetItem }>;
  },

  updateOptionSetItem(
    setId: string,
    itemId: string,
    body: Partial<{
      labelI18n: Record<string, string>;
      sortOrder: number;
      active: boolean;
      imageUrl: string | null;
    }>,
  ) {
    return api.tenantRequest(
      `/option-sets/${encodeURIComponent(setId)}/items/${encodeURIComponent(itemId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    ) as Promise<{ item: OptionSetItem }>;
  },

  rebuildVariantTagIndex() {
    return api.tenantRequest('/settings/catalog-site/rebuild-variant-tags', {
      method: 'POST',
    }) as Promise<{ ok: boolean; tagCount: number; productCount: number }>;
  },

  listTenantExtensions() {
    return api.tenantRequest('/extensions') as Promise<{ extensions: ExtensionCatalogItem[] }>;
  },

  orderTenantExtension(key: string) {
    return api.tenantRequest(`/extensions/${encodeURIComponent(key)}/order`, {
      method: 'POST',
      body: '{}',
    }) as Promise<{ order: ExtensionOrder }>;
  },

  installTenantExtension(key: string) {
    return api.tenantRequest(`/extensions/${encodeURIComponent(key)}/install`, {
      method: 'POST',
      body: '{}',
    }) as Promise<{ extension: { extensionKey: string; enabled: boolean; config: Record<string, unknown> } }>;
  },

  updateTenantExtensionConfig(key: string, config: Record<string, unknown>) {
    return api.tenantRequest(`/extensions/${encodeURIComponent(key)}/config`, {
      method: 'PUT',
      body: JSON.stringify({ config }),
    }) as Promise<{ extension: { extensionKey: string; enabled: boolean; config: Record<string, unknown> } }>;
  },

  getPlatformExtensionCatalog() {
    return api.platformRequest('/extensions') as Promise<{ extensions: PlatformExtensionCatalogEntry[] }>;
  },

  updatePlatformExtensionCatalogEntry(
    key: string,
    body: { price?: number; availability?: ExtensionAvailability },
  ) {
    return api.platformRequest(`/extensions/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ entry: PlatformExtensionCatalogEntry }>;
  },

  listPlatformRoomMockupTemplates(extensionKey: string) {
    return api.platformRequest(
      `/extensions/${encodeURIComponent(extensionKey)}/room-mockup-templates`,
    ) as Promise<{
      templates: Array<{
        id?: string;
        url: string;
        corners: import('./roomMockup').RoomMockupCorners;
        folderPath?: string;
        ready?: boolean;
        label?: string;
        originalFilename?: string;
        sortOrder?: number;
      }>;
      folders: string[];
      entry: PlatformExtensionCatalogEntry;
    }>;
  },

  savePlatformRoomMockupTemplates(
    extensionKey: string,
    body: {
      templates: Array<{
        id?: string;
        url: string;
        corners: import('./roomMockup').RoomMockupCorners;
        folderPath?: string;
        ready?: boolean;
        label?: string;
        originalFilename?: string;
      }>;
      folders?: string[];
    },
  ) {
    return api.platformRequest(
      `/extensions/${encodeURIComponent(extensionKey)}/room-mockup-templates`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    ) as Promise<{
      templates: Array<{
        id?: string;
        url: string;
        corners: import('./roomMockup').RoomMockupCorners;
        folderPath?: string;
        ready?: boolean;
        label?: string;
        originalFilename?: string;
        sortOrder?: number;
      }>;
      folders: string[];
      entry: PlatformExtensionCatalogEntry;
    }>;
  },

  uploadPlatformRoomMockupTemplate(extensionKey: string, file: File) {
    const body = new FormData();
    body.append('file', file);
    return api.platformRequest(
      `/extensions/${encodeURIComponent(extensionKey)}/room-mockup-templates/upload`,
      {
        method: 'POST',
        body,
      },
    ) as Promise<{ url: string; originalFilename?: string }>;
  },

  getPublisherDraft(sourceType: PublisherSourceType, sourceId: string, lang: Language) {
    const params = new URLSearchParams({ sourceType, sourceId, lang });
    return api.tenantRequest(`/publisher/draft?${params}`) as Promise<{ draft: PublisherDraft }>;
  },

  listPublisherProducts(lang: Language, search?: string) {
    const params = new URLSearchParams({ lang });
    const trimmed = search?.trim();
    if (trimmed) params.set('search', trimmed);
    return api.tenantRequest(`/publisher/sources/products?${params}`) as Promise<{
      items: PublisherSourceListItem[];
    }>;
  },

  listPublisherAnnouncements(lang: Language) {
    return api.tenantRequest(`/publisher/sources/announcements?lang=${lang}`) as Promise<{
      items: PublisherSourceListItem[];
    }>;
  },

  listPublisherNotes() {
    return api.tenantRequest('/publisher/sources/notes') as Promise<{
      items: PublisherSourceListItem[];
    }>;
  },

  publisherAiText(body: {
    draftText: string;
    templateId: string;
    customPrompt?: string;
    targetLang: Language;
  }) {
    return api.tenantRequest('/publisher/ai-text', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ text: string }>;
  },

  publisherTranslate(body: { text: string; targetLang: Language }) {
    return api.tenantRequest('/publisher/translate', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ text: string }>;
  },

  getPlatformExtensionOrders(params?: { status?: string; limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs}` : '';
    return api.platformRequest(`/extension-orders${suffix}`) as Promise<{
      rows: ExtensionOrder[];
      total: number;
    }>;
  },

  reviewPlatformExtensionOrder(id: string, action: 'paid' | 'allowed' | 'rejected', adminNote?: string) {
    return api.platformRequest(`/extension-orders/${encodeURIComponent(id)}/review`, {
      method: 'POST',
      body: JSON.stringify({ action, adminNote }),
    }) as Promise<{ ok: boolean; order: ExtensionOrder }>;
  },

  getTenantOverviewMetrics() {
    return api.tenantRequest('/overview-metrics') as Promise<{ metrics: TenantOverviewMetrics }>;
  },

  listTenantOrders() {
    return api.tenantRequest('/orders') as Promise<{ orders: OrderSummary[] }>;
  },

  fetchTenantOrder(id: string) {
    return api.tenantRequest(`/orders/${encodeURIComponent(id)}`) as Promise<{ order: OrderDetail }>;
  },

  updateTenantOrderStatus(id: string, status: OrderStatus) {
    return api.tenantRequest(`/orders/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }) as Promise<{ order: OrderDetail }>;
  },

  async getSystemConfig() {
    const res = await fetch('/api/system/config');
    const data = await parseJson(res);
    if (!res.ok) {
      throw new Error(data.error || 'system_config_failed');
    }
    return data;
  },

  async getLightroomConfig() {
    const res = await fetch('/api/lightroom/config');
    const data = await parseJson(res);
    if (!res.ok) {
      throw new Error(data.error || 'lightroom_config_failed');
    }
    return data as { serviceOrigin: string };
  },

  async getLandingCopy() {
    const res = await fetch('/api/system/landing-copy');
    const data = await parseJson(res);
    if (!res.ok) {
      throw new Error(data.error || 'landing_copy_failed');
    }
    return data;
  },

  async getLandingBlocks() {
    const res = await fetch('/api/system/landing-blocks');
    const data = await parseJson(res);
    if (!res.ok) {
      throw new Error(data.error || 'landing_blocks_failed');
    }
    return data;
  },

  async patchLandingBlocks(blocks: Record<string, boolean>) {
    const res = await fetch('/api/system/landing-blocks', {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ blocks }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'landing_blocks_patch_failed'), {
        data,
        status: res.status,
      });
    }
    return data;
  },

  async reloadLandingContent() {
    const res = await fetch('/api/system/landing-reload', {
      method: 'POST',
      headers: authHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'landing_reload_failed'), {
        data,
        status: res.status,
      });
    }
    return data;
  },

  async reloadSystemConfig() {
    const res = await fetch('/api/system/reload', {
      method: 'POST',
      headers: authHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'reload_failed'), { data, status: res.status });
    }
    return data;
  },

  async platformRequest(path: string, init?: RequestInit) {
    const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
    const res = await fetch(`/api/platform${path}`, {
      ...init,
      headers: {
        ...(isFormData ? platformUploadHeaders() : authHeaders()),
        ...(init?.headers || {}),
      },
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'platform_request_failed'), { data, status: res.status });
    }
    return data;
  },

  getPlatformOverviewMetrics() {
    return api.platformRequest('/overview-metrics');
  },

  getPlatformPlanCatalog() {
    return api.platformRequest('/plan-catalog') as Promise<{
      plans: Array<{
        id: string;
        sortOrder: number;
        storageGb: number;
        priceMonthly: number;
        features: Record<string, boolean>;
      }>;
      featureKeys: string[];
    }>;
  },

  savePlatformPlanCatalog(plans: Array<{
    id: string;
    sortOrder: number;
    storageGb: number;
    priceMonthly: number;
    features: Record<string, boolean>;
  }>) {
    return api.platformRequest('/plan-catalog', {
      method: 'PUT',
      body: JSON.stringify({ plans }),
    }) as Promise<{
      plans: Array<{
        id: string;
        sortOrder: number;
        storageGb: number;
        priceMonthly: number;
        features: Record<string, boolean>;
      }>;
      featureKeys: string[];
    }>;
  },

  getPlatformSeoAudit() {
    return api.platformRequest('/seo-audit') as Promise<{ report: PlatformSeoAuditReport }>;
  },

  runPlatformSeoAudit() {
    return api.platformRequest('/seo-audit', { method: 'POST', body: '{}' }) as Promise<{
      report: PlatformSeoAuditReport;
    }>;
  },

  getPlatformTenantSummaries() {
    return api.platformRequest('/tenant-summaries');
  },

  getPlatformTenant(id: string) {
    return api.platformRequest(`/tenants/${id}`);
  },

  createPlatformTenant(body: {
    slug: string;
    name: string;
    ownerEmail: string;
    contactPhone: string;
    contactAddress: string;
    referredByPromoterId?: string;
    maxProducts?: number;
    uiPresetId?: string | null;
    planId?: string;
    planPaidUntil?: string | null;
    planLifetime?: boolean;
    planStorageGb?: number;
    planPriceMonthly?: number;
    planFeatures?: Record<string, boolean>;
    planCustomized?: boolean;
    planMediaBackup?: boolean;
    allowAnyMedia?: boolean;
    planExtraStorageGb?: number;
    giftCouponCode?: string;
    lang?: Language;
  }) {
    return api.platformRequest('/tenants', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updatePlatformTenant(id: string, body: Record<string, unknown>) {
    return api.platformRequest(`/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  suspendPlatformTenant(id: string) {
    return api.platformRequest(`/tenants/${id}`, { method: 'DELETE' });
  },

  activatePlatformTenant(id: string) {
    return api.platformRequest(`/tenants/${id}/activate`, { method: 'POST', body: '{}' });
  },

  deletePlatformTenantPermanently(id: string, confirmSlug: string) {
    return api.platformRequest(`/tenants/${id}/delete-permanently`, {
      method: 'POST',
      body: JSON.stringify({ confirmSlug }),
    });
  },

  getPlatformAccountDeletions() {
    return api.platformRequest('/account-deletions') as Promise<{
      requests: Array<{
        id: string;
        name: string;
        email: string;
        message: string;
        createdAt: string;
        tenant: { id: string; slug: string; name: string } | null;
      }>;
    }>;
  },

  fulfillPlatformAccountDeletion(id: string, confirmSlug: string) {
    return api.platformRequest(`/account-deletions/${encodeURIComponent(id)}/fulfill`, {
      method: 'POST',
      body: JSON.stringify({ confirmSlug }),
    });
  },

  grantContentManager(tenantId: string) {
    return api.platformRequest(`/tenants/${tenantId}/grants`, { method: 'POST', body: '{}' });
  },

  enterPlatformTenant(tenantId: string) {
    return api.platformRequest(`/tenants/${tenantId}/enter`, {
      method: 'POST',
      body: '{}',
    }) as Promise<{ enterToken: string; slug: string; name: string }>;
  },

  async consumePlatformEnter(enterToken: string) {
    const res = await fetch('/api/auth/platform-enter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enterToken }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'platform_enter_failed'), { data, status: res.status });
    }
    setStoredToken(data.token);
    return data as { token: string; session: AuthSession };
  },

  async consumeSignupEnter(enterToken: string) {
    const res = await fetch('/api/auth/signup-enter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enterToken }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'signup_enter_failed'), { data, status: res.status });
    }
    setStoredToken(data.token);
    return data as { token: string; session: AuthSession };
  },

  async consumeTripEnter(enterToken: string) {
    const res = await fetch('/api/auth/trip-enter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enterToken }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'trip_enter_failed'), { data, status: res.status });
    }
    setStoredToken(data.token);
    return data as { token: string; session: AuthSession; galleryId: string };
  },

  getTenantTrip() {
    return api.tenantRequest('/trip') as Promise<{
      trip: {
        galleryId: string;
        email: string;
        days: number;
        expiresAt: string;
        deleteAt: string;
        closed: boolean;
      } | null;
    }>;
  },

  revokeContentManager(tenantId: string) {
    return api.platformRequest(`/tenants/${tenantId}/grants`, { method: 'DELETE' });
  },

  runPlatformDbHealth() {
    return api.platformRequest('/maintenance/db-health', { method: 'POST', body: '{}' });
  },

  scanPlatformUploadOrphans() {
    return api.platformRequest('/maintenance/upload-orphans') as Promise<{
      orphans: Array<{
        tenantSlug: string;
        area: string;
        fileName: string;
        url: string;
        sizeBytes: number;
      }>;
      fileCount: number;
      totalBytes: number;
    }>;
  },

  cleanupPlatformUploadOrphans() {
    return api.platformRequest('/maintenance/upload-orphans/cleanup', {
      method: 'POST',
      body: '{}',
    }) as Promise<{ ok: boolean; deleted: number; freedBytes: number; scanned: number }>;
  },

  getPlatformAuditLog(params?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs}` : '';
    return api.platformRequest(`/audit-log${suffix}`);
  },

  clearPlatformAuditLog() {
    return api.platformRequest('/audit-log', { method: 'DELETE' });
  },

  getPlatformBackupStatus() {
    return api.platformRequest('/backups/status') as Promise<PlatformBackupStatus>;
  },

  listPlatformBackups() {
    return api.platformRequest('/backups/list') as Promise<{ backups: PlatformBackupFile[] }>;
  },

  createPlatformBackup() {
    return api.platformRequest('/backups/create', { method: 'POST', body: '{}' }) as Promise<{
      ok: boolean;
      filename: string;
      backup: PlatformBackupStatus;
    }>;
  },

  updatePlatformBackupCron(cronExpression: string) {
    return api.platformRequest('/backups/cron', {
      method: 'PUT',
      body: JSON.stringify({ cronExpression }),
    }) as Promise<{ ok: boolean; backup: PlatformBackupStatus }>;
  },

  setPlatformBackupCronEnabled(enabled: boolean) {
    return api.platformRequest('/backups/cron-enabled', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }) as Promise<{ ok: boolean; backup: PlatformBackupStatus }>;
  },

  runPlatformBackupCronNow() {
    return api.platformRequest('/backups/run-cron-now', {
      method: 'POST',
      body: '{}',
    }) as Promise<{ ok: boolean; filename: string; backup: PlatformBackupStatus }>;
  },

  restorePlatformBackup(filename: string, confirmToken: string) {
    return api.platformRequest('/backups/restore', {
      method: 'POST',
      body: JSON.stringify({ filename, confirmToken }),
    }) as Promise<{
      ok: boolean;
      filename: string;
      preRestoreBackup: string;
      backup: PlatformBackupStatus;
    }>;
  },

  getPlatformJoinRequests(params?: { status?: string; limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs}` : '';
    return api.platformRequest(`/join-requests${suffix}`) as Promise<{
      rows: PlatformJoinRequest[];
      total: number;
    }>;
  },

  reviewPlatformJoinRequest(id: string) {
    return api.platformRequest(`/join-requests/${id}/review`, {
      method: 'POST',
      body: '{}',
    }) as Promise<{ ok: boolean; request: PlatformJoinRequest }>;
  },

  getReferralApplications(params?: { status?: string; limit?: number; offset?: number }) {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const suffix = q.toString() ? `?${q}` : '';
    return api.platformRequest(`/referral-applications${suffix}`) as Promise<{
      rows: ReferralApplication[];
      total: number;
    }>;
  },

  reviewReferralApplication(id: string) {
    return api.platformRequest(`/referral-applications/${id}/review`, {
      method: 'POST',
      body: '{}',
    }) as Promise<{ ok: boolean; application: ReferralApplication }>;
  },

  approveReferralApplication(
    id: string,
    body: {
      email: string;
      password?: string;
      displayName?: string;
      locality?: 'local' | 'foreign';
      referralPayout: string | { kind: 'percent' | 'fixed'; value: number };
    },
  ) {
    return api.platformRequest(`/referral-applications/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{
      ok: boolean;
      partner: ReferralPartner;
      temporaryPassword: string | null;
      telegramInvite?: { deepLink: string; miniAppUrl: string; expiresInMinutes: number } | null;
    }>;
  },

  listReferralPartners() {
    return api.platformRequest('/referral-partners') as Promise<{ partners: ReferralPartner[] }>;
  },

  updateReferralPartner(
    id: string,
    body: {
      displayName?: string;
      phone?: string;
      telegram?: string;
      locality?: 'local' | 'foreign';
      referralPayout?: string | { kind: 'percent' | 'fixed'; value: number };
      active?: boolean;
      referralPaused?: boolean;
      regenerateToken?: boolean;
    },
  ) {
    return api.platformRequest(`/referral-partners/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }) as Promise<{ ok: boolean; partner: ReferralPartner }>;
  },

  deleteReferralPartner(id: string, confirmEmail: string) {
    return api.platformRequest(`/referral-partners/${id}/delete`, {
      method: 'POST',
      body: JSON.stringify({ confirmEmail }),
    }) as Promise<{ ok: boolean }>;
  },

  listReferralConversions(params?: { limit?: number; offset?: number }) {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const suffix = q.toString() ? `?${q}` : '';
    return api.platformRequest(`/referral-conversions${suffix}`) as Promise<{
      rows: ReferralConversion[];
      total: number;
    }>;
  },

  closeReferralDeal(body: {
    tenantId: string;
    promoterId?: string;
    basisAmountIls: number;
    note?: string;
  }) {
    return api.platformRequest('/referral-conversions/close-deal', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ ok: boolean; conversion: ReferralConversion }>;
  },

  listReferralPayouts(params?: { status?: string; limit?: number; offset?: number }) {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const suffix = q.toString() ? `?${q}` : '';
    return api.platformRequest(`/referral-payouts${suffix}`) as Promise<{
      rows: ReferralPayoutRequest[];
      total: number;
    }>;
  },

  runReferralPayoutsNow(body?: { year?: number; month?: number }) {
    return api.platformRequest('/referral-payouts/run-now', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }) as Promise<{
      ok: boolean;
      year?: number;
      month?: number;
      created?: ReferralPayoutRequest[];
      skippedPartners?: Array<{ promoterId: string; reason: string; available?: number }>;
    }>;
  },

  fulfillReferralPayout(id: string) {
    return api.platformRequest(`/referral-payouts/${id}/fulfill`, {
      method: 'POST',
      body: '{}',
    }) as Promise<{ ok: boolean; request: ReferralPayoutRequest }>;
  },

  getReferralProgramSettings() {
    return api.platformRequest('/referral-program-settings') as Promise<{
      referralProgram: import('../types').ReferralProgramConfig;
    }>;
  },

  saveReferralProgramSettings(body: import('../types').ReferralProgramConfig) {
    return api.platformRequest('/referral-program-settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ ok: boolean; referralProgram: import('../types').ReferralProgramConfig }>;
  },

  rejectReferralPayout(id: string, note?: string) {
    return api.platformRequest(`/referral-payouts/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }) as Promise<{ ok: boolean; request: ReferralPayoutRequest }>;
  },

  createPartnerTelegramInvite(id: string) {
    return api.platformRequest(`/referral-partners/${id}/telegram-invite`, {
      method: 'POST',
      body: '{}',
    }) as Promise<{ ok: boolean; deepLink: string; miniAppUrl: string; expiresInMinutes: number }>;
  },

  async partnerAuth(initData: string) {
    const res = await fetch('/api/partner/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      const err = new Error(data.error || 'partner_auth_failed') as Error & { data?: unknown };
      err.data = data;
      throw err;
    }
    return data as { token: string; session: AuthSession } & PartnerCabinetResponse;
  },

  async partnerLogin(email: string, password: string) {
    const res = await fetch('/api/partner/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      const err = new Error(data.error || 'partner_login_failed') as Error & { data?: unknown; status?: number };
      err.data = data;
      err.status = res.status;
      throw err;
    }
    return data as { token: string; session: AuthSession } & PartnerCabinetResponse;
  },

  async changePartnerPassword(currentPassword: string, newPassword: string) {
    const res = await fetch('/api/partner/password', {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      const err = new Error(data.error || 'partner_password_failed') as Error & { data?: unknown };
      err.data = data;
      throw err;
    }
    return data as { ok: boolean };
  },

  async getPartnerCabinet() {
    const res = await fetch('/api/partner/cabinet', { headers: authHeaders() });
    const data = await parseJson(res);
    if (!res.ok) {
      const err = new Error(data.error || 'partner_cabinet_failed') as Error & { data?: unknown };
      err.data = data;
      throw err;
    }
    return data as PartnerCabinetResponse;
  },

  async updatePartnerPayoutDetails(body: ReferralPayoutDetails) {
    const res = await fetch('/api/partner/payout-details', {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      const err = new Error(data.error || 'partner_payout_details_failed') as Error & { data?: unknown };
      err.data = data;
      throw err;
    }
    return data as PartnerCabinetResponse;
  },

  async updatePartnerNotifyLang(lang: Language | 'auto' | null) {
    const res = await fetch('/api/partner/notify-lang', {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ lang: lang === null ? 'auto' : lang }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      const err = new Error(data.error || 'partner_notify_lang_failed') as Error & { data?: unknown };
      err.data = data;
      throw err;
    }
    return data as PartnerCabinetResponse;
  },

  listPlatformStaff() {
    return api.platformRequest('/staff') as Promise<{ staff: PlatformStaffMember[] }>;
  },

  createPlatformStaffMember(body: { email: string; password: string }) {
    return api.platformRequest('/staff', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ member: PlatformStaffMember }>;
  },

  updatePlatformStaffMember(id: string, body: { active: boolean }) {
    return api.platformRequest(`/staff/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }) as Promise<{ member: PlatformStaffMember }>;
  },

  resetPlatformStaffPassword(id: string, password: string) {
    return api.platformRequest(`/staff/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }) as Promise<{ ok: boolean }>;
  },

  deletePlatformStaffMember(id: string) {
    return api.platformRequest(`/staff/${id}`, { method: 'DELETE' }) as Promise<{ ok: boolean }>;
  },

  async tenantRequest(path: string, init?: RequestInit) {
    const res = await fetch(`/api/tenant${path}`, {
      ...init,
      headers: { ...authHeaders(), ...tenantScopeHeaders(), ...(init?.headers || {}) },
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'tenant_request_failed'), { data, status: res.status });
    }
    return data;
  },

  listGalleries(options: { sizes?: boolean } = {}) {
    return api.tenantRequest(options.sizes ? '/galleries?sizes=1' : '/galleries') as Promise<{
      galleries: PhotographerGallery[];
      catalogOfWorksPlan: boolean;
      planFeatures?: GalleryPlanFeatures;
      planBackup?: boolean;
      sizes?: Record<string, { photos: number; bytes: number; backupBytes: number }>;
    }>;
  },

  shareGallery(id: string, addresses: string, allowCopy: boolean) {
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}/share`, {
      method: 'POST',
      body: JSON.stringify({ addresses, allowCopy }),
    }) as Promise<{ sent: string[]; skipped: string[]; missing: string[] }>;
  },

  listGalleryShares() {
    return api.tenantRequest('/gallery-shares') as Promise<{
      shares: Array<{
        id: string;
        choice: 'link' | null;
        allowCopy: boolean;
        onSite: boolean;
        name: string;
        coverUrl: string;
        href: string;
        authorName: string;
        authorHref: string;
      }>;
    }>;
  },

  chooseGalleryShare(id: string, choice: 'link' | 'copy') {
    return api.tenantRequest(`/gallery-shares/${encodeURIComponent(id)}/choose`, {
      method: 'POST',
      body: JSON.stringify({ choice }),
    }) as Promise<{ ok: boolean; choice: string; galleryId?: string }>;
  },

  setGalleryShareOnSite(id: string, onSite: boolean) {
    return api.tenantRequest(`/gallery-shares/${encodeURIComponent(id)}/site`, {
      method: 'PUT',
      body: JSON.stringify({ onSite }),
    }) as Promise<{ ok: boolean; onSite: boolean }>;
  },

  setPublicLikes(id: string, body: { actor: string; email?: string; photoIds: string[] }) {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/likes`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'gallery_failed');
      return data as { counts: Record<string, number> };
    });
  },

  syncGalleryCatalog(id: string) {
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}/catalog`, {
      method: 'POST',
    }) as Promise<{
      added: number;
      updated: number;
      skipped?: number;
      empty: boolean;
      cabinetOpened: boolean;
      productCount?: number;
      productMax?: number;
    }>;
  },

  listBooks() {
    return api.tenantRequest('/books') as Promise<{
      books: Array<{
        galleryId: string;
        code: string;
        name: string;
        coverPhotoId: string | null;
        albumName: string;
        expiresAt: string;
        clientName: string;
      }>;
      globalBooks: GlobalBook[];
      planFeatures: GalleryPlanFeatures;
    }>;
  },

  listGlobalBookAlbums() {
    return api.tenantRequest('/global-books/albums') as Promise<{
      albums: Array<{ id: string; name: string; nameI18n: Partial<Record<Language, string>>; photoCount: number }>;
    }>;
  },

  getGlobalBook(id: string) {
    return api.tenantRequest(`/global-books/${encodeURIComponent(id)}`) as Promise<{
      book: GlobalBook;
      photos: GlobalBookPhoto[];
      cover: GlobalBookPhoto | null;
    }>;
  },

  saveGlobalBook(id: string | null, body: GlobalBookWrite) {
    return api.tenantRequest(id ? `/global-books/${encodeURIComponent(id)}` : '/global-books', {
      method: id ? 'PUT' : 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ book: GlobalBook }>;
  },

  deleteGlobalBook(id: string) {
    return api.tenantRequest(`/global-books/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }) as Promise<{ ok: true }>;
  },

  getGallery(id: string) {
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}`) as Promise<{
      gallery: PhotographerGallery;
      planFeatures: GalleryPlanFeatures;
      planBackup: boolean;
      allowAnyMedia: boolean;
    }>;
  },

  getGallerySettingsContext() {
    return api.tenantRequest('/galleries/settings-context') as Promise<{
      planFeatures: GalleryPlanFeatures;
      planBackup: boolean;
      allowAnyMedia: boolean;
    }>;
  },

  getPortfolio() {
    return api.tenantRequest('/portfolio') as Promise<PortfolioSettings>;
  },

  savePortfolio(body: {
    showOnSite: boolean;
    showOnCard: boolean;
    items: Array<{ kind: PortfolioItem['kind']; galleryId: string; photoId: string | null }>;
  }) {
    return api.tenantRequest('/portfolio', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<PortfolioSettings>;
  },

  createGallery(body: { name: string; settings: PhotographerGallerySettings }) {
    return api.tenantRequest('/galleries', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ gallery: PhotographerGallery }>;
  },

  generateGalleryBotCodes(id: string) {
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}/bot-codes`, {
      method: 'POST',
    }) as Promise<{ botCodes: GalleryBotCodes }>;
  },

  updateGallery(id: string, body: { name: string; settings: PhotographerGallerySettings }) {
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ gallery: PhotographerGallery; catalogHidden?: number }>;
  },

  getManualPayment(planId?: string, wantBackup?: boolean, billingMonths: 1 | 12 = 1, wantShowcase?: boolean) {
    const params = new URLSearchParams();
    if (planId) params.set('planId', planId);
    if (wantBackup) params.set('wantBackup', '1');
    if (wantShowcase) params.set('wantShowcase', '1');
    if (billingMonths === 12) params.set('billingMonths', '12');
    const query = params.toString();
    return api.tenantRequest(`/manual-payment${query ? `?${query}` : ''}`) as Promise<{
      methods: {
        bitPhone: string;
        paybox: string;
        bankDetails: string;
        paypal: string;
        whatsapp: string;
        confirmEmail: string;
      };
      payments: Array<{
        id: string;
        periodOn: string;
        periodMonths: 1 | 12;
        kind: 'period' | 'upgrade' | 'extra';
        dueOn: string;
        status: 'waiting' | 'received' | 'cancelled';
        planId: string;
        priceUsd: number;
        priceIls: number;
        mediaBackup: boolean;
      }>;
      quote: {
        planId: string;
        mode: 'new' | 'next' | 'upgrade';
        remainingMonths: number;
        priceUsd: number;
        priceIls: number;
        ilsPerUsd: number;
        dueOn: string;
        mediaBackup: boolean;
        catalogOfWorks: boolean;
        giftCouponBody?: string;
        billingMonths: 1 | 12;
      } | null;
    }>;
  },

  quoteExtraStorage(gb: number) {
    return api.tenantRequest(`/extra-storage?gb=${encodeURIComponent(String(gb))}`) as Promise<{
      gb: number;
      extraStorageGb: number;
      storageGb: number;
      monthlyUsd: number;
      mode: 'next' | 'bill' | 'year';
      priceUsd: number;
      priceIls: number;
      dueOn: string;
    }>;
  },

  buyExtraStorage(gb: number) {
    return api.tenantRequest('/extra-storage', {
      method: 'POST',
      body: JSON.stringify({ gb }),
    }) as Promise<{
      ok: boolean;
      gb: number;
      extraStorageGb: number;
      storageGb: number;
      monthlyUsd: number;
      mode: 'next' | 'bill' | 'year';
      priceUsd: number;
      priceIls: number;
      dueOn: string;
    }>;
  },

  buyManualPlan(body: {
    planId: string;
    wantBackup: boolean;
    wantShowcase: boolean;
    billingMonths: 1 | 12;
    lang: string;
  }) {
    return api.tenantRequest('/manual-payment', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{
      ok: boolean;
      sourcePlanId: string;
      mediaBackup: boolean;
      catalogOfWorks: boolean;
      paidUntil: string;
      priceUsd: number;
      priceIls: number;
    }>;
  },

  getManualPaymentSettings() {
    return api.platformRequest('/manual-payment-settings') as Promise<{
      manualPayment: {
        bitPhone: string;
        paybox: string;
        bankDetails: string;
        paypal: string;
        whatsapp: string;
        confirmEmail: string;
      };
    }>;
  },

  saveManualPaymentSettings(body: {
    bitPhone: string;
    paybox: string;
    bankDetails: string;
    paypal: string;
    whatsapp: string;
    confirmEmail: string;
  }) {
    return api.platformRequest('/manual-payment-settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ ok: boolean }>;
  },

  listManualPayments() {
    return api.platformRequest('/manual-payments') as Promise<{
      payments: Array<{
        id: string;
        periodOn: string;
        periodMonths: 1 | 12;
        kind: 'period' | 'upgrade' | 'extra';
        dueOn: string;
        status: 'waiting' | 'received' | 'cancelled';
        planId: string;
        priceUsd: number;
        priceIls: number;
        mediaBackup: boolean;
        extraStorageGb?: number;
        tenantName: string;
        ownerEmail: string;
      }>;
    }>;
  },

  receiveManualPayment(id: string) {
    return api.platformRequest(`/manual-payments/${encodeURIComponent(id)}/receive`, {
      method: 'POST',
    }) as Promise<{ ok: boolean }>;
  },

  buyPlanBackupTest(wantBackup: boolean, lang: string) {
    return api.tenantRequest('/plan-backup-test', {
      method: 'POST',
      body: JSON.stringify({ wantBackup, lang }),
    }) as Promise<{ ok: boolean; test: boolean; mediaBackup: boolean }>;
  },

  getBackupGap() {
    return api.tenantRequest('/plan-backup-gap') as Promise<{
      albums: Array<{ id: string; name: string; missingBytes: number; missingFiles: number }>;
    }>;
  },

  queueBackupAlbums(galleryIds: string[], lang: string) {
    return api.tenantRequest('/plan-backup-queue', {
      method: 'POST',
      body: JSON.stringify({ galleryIds, lang }),
    }) as Promise<{ ok: boolean; bytes: number; hours: number }>;
  },

  getOnboarding() {
    return api.tenantRequest('/onboarding') as Promise<{
      onboarding: {
        step: OnboardingStep;
        done: boolean;
        seen: boolean;
        trip: boolean;
        cardOpen: boolean;
        accountEmail: string;
      } | null;
    }>;
  },

  saveOnboarding(body: { step: OnboardingStep; done?: boolean }) {
    return api.tenantRequest('/onboarding', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ onboarding: { step: OnboardingStep; done: boolean } }>;
  },

  saveOnboardingCard(body: {
    lang: Language;
    galleryId?: string | null;
    photoIds?: string[];
    services?: Array<{ title: string; text: string }>;
  }) {
    return api.tenantRequest('/onboarding/card', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ worksAdded: number; servicesAdded: number }>;
  },

  getTenantPlanCatalog() {
    return api.tenantRequest('/plan-catalog') as Promise<{
      plans: Array<{
        id: string;
        sortOrder: number;
        storageGb: number;
        priceMonthly: number;
        features: Record<string, boolean>;
      }>;
      featureKeys: string[];
      current: {
        sourcePlanId: string;
        customized: boolean;
        mediaBackup?: boolean;
        lifetime?: boolean;
        paidUntil?: string | null;
        priceMonthly?: number;
        extraStorageGb?: number;
        catalogOfWorks?: boolean;
        giftCoupon?: { code: string; body: string; remainingMonths: number } | null;
      };
      planChangeReady: boolean;
    }>;
  },

  listPlatformGiftCoupons() {
    return api.platformRequest('/gift-coupons') as Promise<{
      coupons: Array<{
        code: string;
        body: string;
        freeShowcaseMonths: number;
        validFrom: string | null;
        validUntil: string | null;
        active: boolean;
      }>;
    }>;
  },

  createPlatformGiftCoupon(body: {
    code: string;
    body: string;
    freeShowcaseMonths: number;
    validFrom?: string;
    validUntil?: string;
  }) {
    return api.platformRequest('/gift-coupons', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  redeemGiftCoupon(code: string) {
    return api.tenantRequest('/gift-coupon', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }) as Promise<{
      giftCoupon: { code: string; body: string; remainingMonths: number };
    }>;
  },

  galleryInboxSummary() {
    return api.tenantRequest('/galleries/inbox-summary') as Promise<{
      reviews: { total: number; fresh: number };
      requests: { total: number; fresh: number };
      albums: number;
      disk: { usedBytes: number; storageGb: number; backupBytes?: number };
      trash?: { count: number; bytes: number };
      plan: {
        sourcePlanId: string;
        customized: boolean;
        mediaBackup?: boolean;
        visitingCard?: boolean;
        site?: boolean;
        catalogOfWorks?: boolean;
        giftCoupon?: string;
      };
      backupQueue?: { count: number; albums: Array<{ id: string; name: string }> };
      planChangeReady?: boolean;
      onboarding?: { profile: boolean; contacts: boolean; album: boolean };
      catalogShowcase?: {
        state: 'pending' | 'ready' | 'live' | 'off';
        url: string;
        productCount?: number;
        productMax?: number;
      } | null;
      stats?: Array<{
        id: string;
        name: string;
        nameI18n: { ru: string; en: string; he: string };
        coverPhotoId?: string | null;
        photos?: number;
        bytes?: number;
        backupBytes?: number;
        visits: number;
        views: number;
        downloads: number;
        reviews: number;
      }>;
    }>;
  },

  activateCatalogShowcase() {
    return api.tenantRequest('/galleries/catalog-showcase/activate', {
      method: 'POST',
    }) as Promise<{
      catalogShowcase: {
        state: 'live';
        url: string;
        productCount?: number;
        productMax?: number;
      };
    }>;
  },

  notePublicGalleryVisit(id: string) {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/visit`, {
      method: 'POST',
      credentials: 'include',
    }).then(() => undefined);
  },

  notePublicGalleryView(id: string) {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/view`, {
      method: 'POST',
      credentials: 'include',
    }).then(() => undefined);
  },

  listInboxReviews() {
    return api.tenantRequest('/galleries/inbox/reviews') as Promise<{
      reviews: Array<{
        id: string;
        guestName: string;
        body: string;
        galleryName: string;
        createdAt: string;
        fresh: boolean;
        publishedAt: string | null;
      }>;
      publishReviews: boolean;
    }>;
  },

  publishInboxReview(reviewId: string) {
    return api.tenantRequest(`/galleries/inbox/reviews/${encodeURIComponent(reviewId)}/publish`, {
      method: 'POST',
    }) as Promise<{ ok: true; publishedAt: string }>;
  },

  listPublishedInbox() {
    return api.tenantRequest('/galleries/inbox/published') as Promise<{
      reviews: Array<{
        id: string;
        guestName: string;
        body: string;
        galleryName: string;
        publishedAt: string;
      }>;
    }>;
  },

  unpublishInboxReview(reviewId: string) {
    return api.tenantRequest(`/galleries/inbox/reviews/${encodeURIComponent(reviewId)}/unpublish`, {
      method: 'POST',
    }) as Promise<{ ok: true }>;
  },

  listInboxRequests() {
    return api.tenantRequest('/galleries/inbox/requests') as Promise<{
      requests: Array<{
        id: string;
        guestName: string;
        contact: string;
        galleryId: string;
        galleryName: string;
        createdAt: string;
        fresh: boolean;
        photos: Array<{ id: string; originalName: string }>;
      }>;
    }>;
  },

  listPublicReviews() {
    return fetch('/api/public/reviews', { credentials: 'include' }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'gallery_failed');
      return data as { reviews: Array<{ guestName: string; body: string; galleryName: string }> };
    });
  },

  listPublicSiteAlbumFrames(id: string) {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/site-frames`).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'gallery_failed');
      return data as {
        photoIds: string[];
        frames?: Array<{ id: string; width: number | null; height: number | null }>;
      };
    });
  },

  getPublicGallery(id: string, bookCode = '', showCode = '') {
    const params = new URLSearchParams();
    if (bookCode) params.set('book', bookCode);
    if (showCode) params.set('show', showCode);
    const query = params.toString() ? `?${params}` : '';
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}${query}`, {
      credentials: 'include',
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'gallery_failed');
      return data as {
        gallery: {
          name: string;
          nameI18n: { ru: string; en: string; he: string };
          locked: boolean;
          titleFont: GalleryTitleFont;
          hidePoweredBy?: boolean;
          visitingCard?: boolean;
          layout?: GalleryLayout;
          sort?: GallerySort;
          watermark?: GalleryWatermark;
          watermarkText?: string;
          watermarkOpacity?: number;
          watermarkPlace?: GalleryWatermarkPlace;
          watermarkPng?: boolean;
          coverPhotoId?: string | null;
          caption?: 'off' | 'name' | 'date' | 'both';
          captionX?: number;
          captionY?: number;
          captionSize?: number;
          coverFocus?: number;
          hideCoverPhoto?: boolean;
          shootDate?: string;
          sections?: GallerySection[];
          selectionEnabled?: boolean;
          likesEnabled?: boolean;
          likeCounts?: Record<string, number>;
          photoCredit?: { name: string; href: string } | null;
          selectionSync?: boolean;
          selectionSlideshow?: boolean;
          viewOnly?: boolean;
          downloadLimit?: number | null;
          contacts?: {
            phone?: string;
            email?: string;
            whatsapp?: string;
            telegram?: string;
          };
          photos?: GalleryPhoto[];
          digitalAlbum?: {
            watermark?: boolean;
            order?: 'sections' | 'pile';
            download?: boolean;
            scope?: 'all' | 'sections' | 'frames';
            frameIds?: string[];
            sectionIds?: string[];
            sortBy?: 'date' | 'name';
            sortDir?: 'asc' | 'desc';
            coverPhotoId?: string;
            caption?: 'off' | 'name' | 'date' | 'both';
            captionX?: number;
            captionY?: number;
            coverFocus?: number;
            title?: string;
            background?: string;
            frameColor?: string;
            frameWidth?: number;
            autoplay?: boolean;
            loop?: boolean;
            expired?: boolean;
            photographerName?: string;
          } | null;
        };
      };
    });
  },

  publicGalleryPreview(galleryId: string, photoId: string) {
    return fetch(
      `/api/public/galleries/${encodeURIComponent(galleryId)}/photos/${encodeURIComponent(photoId)}/preview`,
      { credentials: 'include' },
    ).then((res) => readPreviewLink(res));
  },

  getPublicGlobalBook(code: string) {
    return fetch(`/api/public/books/${encodeURIComponent(code)}`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw Object.assign(new Error(data.error || 'gallery_failed'), { status: res.status });
        return data as { book: PublicGlobalBook };
      });
  },

  publicGlobalBookPreview(code: string, galleryId: string, photoId: string) {
    return fetch(
      `/api/public/books/${encodeURIComponent(code)}/photos/${encodeURIComponent(galleryId)}/${encodeURIComponent(photoId)}/preview`,
      { credentials: 'include' },
    ).then((res) => readPreviewLink(res));
  },

  publicGalleryPlay(galleryId: string, photoId: string) {
    return fetch(
      `/api/public/galleries/${encodeURIComponent(galleryId)}/photos/${encodeURIComponent(photoId)}/play`,
      { credentials: 'include' },
    ).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok || typeof data.url !== 'string' || !data.url) throw new Error('preview_failed');
      return data as { url: string; expiresIn: number };
    });
  },

  publicGalleryFile(galleryId: string, photoId: string) {
    return fetch(
      `/api/public/galleries/${encodeURIComponent(galleryId)}/photos/${encodeURIComponent(photoId)}/file`,
      { credentials: 'include' },
    ).then(async (res) => {
      if (!res.ok) throw new Error('preview_failed');
      return res.blob();
    });
  },

  publicGalleryWatermark(galleryId: string) {
    return fetch(`/api/public/galleries/${encodeURIComponent(galleryId)}/watermark`, {
      credentials: 'include',
    }).then(async (res) => {
      if (!res.ok) throw new Error('preview_failed');
      return res.blob();
    });
  },

  unlockPublicGallery(id: string, pin: string) {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/unlock`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'gallery_failed');
      return data as { ok: true };
    });
  },

  sendPublicReview(id: string, body: { name: string; text: string }) {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/review`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'review_failed');
    });
  },

  getPublicSelectionSync(id: string, email: string) {
    const query = new URLSearchParams({ email });
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/selection-sync?${query}`, {
      credentials: 'include',
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'selection_sync_failed');
      const photoIds = Array.isArray(data.photoIds)
        ? data.photoIds.filter((item: unknown): item is string => typeof item === 'string')
        : [];
      return { photoIds };
    });
  },

  savePublicSelectionSync(id: string, email: string, photoIds: string[]) {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/selection-sync`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, photoIds }),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'selection_sync_failed');
      return data as { photoIds: string[] };
    });
  },

  savePublicSlideshow(id: string, photoIds: string[], shuffled = false) {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/slideshow`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photoIds, shuffled }),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok || typeof data.code !== 'string' || !data.code) {
        throw new Error(data.error || 'slideshow_failed');
      }
      return { code: data.code };
    });
  },

  getPublicSlideshow(id: string, code: string) {
    return fetch(
      `/api/public/galleries/${encodeURIComponent(id)}/slideshow/${encodeURIComponent(code)}`,
      { credentials: 'include' },
    ).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'slideshow_failed');
      const photoIds = Array.isArray(data.photoIds)
        ? data.photoIds.filter((item: unknown): item is string => typeof item === 'string')
        : [];
      return { photoIds, shuffled: data.shuffled === true };
    });
  },

  sendPublicSelection(
    id: string,
    body: { name: string; email: string; phone: string; photoIds: string[] },
  ) {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/selection`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'selection_failed');
      return { id: typeof data.id === 'string' ? data.id : '' };
    });
  },

  downloadPublicOriginal(galleryId: string, photoId: string) {
    return fetch(
      `/api/public/galleries/${encodeURIComponent(galleryId)}/photos/${encodeURIComponent(photoId)}/original`,
      { credentials: 'include' },
    ).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok || typeof data.url !== 'string' || !data.url) {
        throw new Error(data.error || 'download_failed');
      }
      return data.url as string;
    });
  },

  downloadPublicAlbum(id: string) {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/archive-album`, {
      method: 'POST',
      credentials: 'include',
    }).then(readArchiveFiles);
  },

  downloadPublicDigitalAlbum(id: string, photoIds: string[], bookCode = '') {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/archive-digital`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photoIds, book: bookCode }),
    }).then(readArchiveFiles);
  },

  downloadPublicSelection(id: string, photoIds: string[]) {
    return fetch(`/api/public/galleries/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photoIds }),
    }).then(readArchiveFiles);
  },

  deleteGallery(id: string, options?: { dropOutside?: boolean }) {
    const query = options?.dropOutside ? '?dropOutside=1' : '';
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}${query}`, {
      method: 'DELETE',
    }) as Promise<{ ok: true; kept?: boolean }>;
  },

  downloadGalleryArchive(id: string, photoIds?: string[]) {
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
      ...(photoIds ? { body: JSON.stringify({ photoIds }) } : {}),
    }) as Promise<{ files: { name: string; url: string; size: number }[] }>;
  },

  uploadGalleryPhotos(id: string, files: File[], sectionId?: string | null) {
    const body = new FormData();
    files.forEach((file) => body.append('files', file));
    if (sectionId) body.append('sectionId', sectionId);
    return fetch(`/api/tenant/galleries/${encodeURIComponent(id)}/photos`, {
      method: 'POST',
      headers: tenantUploadHeaders(),
      body,
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'galleries_save_failed');
      return data as { photos: GalleryPhoto[]; coverPhotoId: string | null };
    });
  },

  uploadGalleryOriginal(
    id: string,
    file: File,
    sectionId?: string | null,
    onProgress?: (loaded: number, total: number) => void,
    onPreparing?: () => void,
    signal?: AbortSignal,
    twin: { replacePhotoId?: string; asCopy?: boolean } = {},
  ) {
    const named = {
      originalName: file.name,
      ...(sectionId ? { sectionId } : {}),
    };
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}/photos/direct`, {
      method: 'POST',
      body: JSON.stringify({ byteSize: file.size, ...named }),
    }).then(async (ticket: { photoId?: string; uploadUrl?: string; headers?: Record<string, string> }) => {
      if (signal?.aborted) throw new Error('upload_aborted');
      if (!ticket?.photoId || !ticket.uploadUrl) throw new Error('photo_upload_failed');
      await putSignedFile(ticket.uploadUrl, ticket.headers || {}, file, onProgress, 'photo_upload_failed', signal);
      onPreparing?.();
      return api.tenantRequest(
        `/galleries/${encodeURIComponent(id)}/photos/direct/complete`,
        {
          method: 'POST',
          body: JSON.stringify({
            photoId: ticket.photoId,
            ...named,
            ...(twin.replacePhotoId ? { replacePhotoId: twin.replacePhotoId } : {}),
            ...(twin.asCopy ? { asCopy: true } : {}),
          }),
        },
      ) as Promise<{
        photos: GalleryPhoto[];
        photo: GalleryPhoto;
        coverPhotoId: string | null;
        replacedPhotoId?: string | null;
      }>;
    });
  },

  uploadGalleryVideo(
    id: string,
    file: File,
    sectionId?: string | null,
    onProgress?: (loaded: number, total: number) => void,
    signal?: AbortSignal,
    twin: { replacePhotoId?: string; asCopy?: boolean } = {},
  ) {
    const named = {
      originalName: file.name,
      ...(sectionId ? { sectionId } : {}),
    };
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}/videos/direct`, {
      method: 'POST',
      body: JSON.stringify({ byteSize: file.size, ...named }),
    }).then(async (ticket: { photoId?: string; uploadUrl?: string; headers?: Record<string, string> }) => {
      if (signal?.aborted) throw new Error('upload_aborted');
      if (!ticket?.photoId || !ticket.uploadUrl) throw new Error('video_upload_failed');
      await putSignedFile(ticket.uploadUrl, ticket.headers || {}, file, onProgress, 'video_upload_failed', signal);
      return api.tenantRequest(
        `/galleries/${encodeURIComponent(id)}/videos/direct/complete`,
        {
          method: 'POST',
          body: JSON.stringify({
            photoId: ticket.photoId,
            ...named,
            ...(twin.replacePhotoId ? { replacePhotoId: twin.replacePhotoId } : {}),
            ...(twin.asCopy ? { asCopy: true } : {}),
          }),
        },
      ) as Promise<{ photos: GalleryPhoto[]; photo: GalleryPhoto; replacedPhotoId?: string | null }>;
    });
  },

  galleryPhotoFile(galleryId: string, photoId: string) {
    return fetch(
      `/api/tenant/galleries/${encodeURIComponent(galleryId)}/photos/${encodeURIComponent(photoId)}/file`,
      { headers: tenantUploadHeaders() },
    ).then(async (res) => {
      if (!res.ok) throw new Error('preview_failed');
      return res.blob();
    });
  },

  createGallerySection(id: string, name: string) {
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}/sections`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }) as Promise<{ section: GallerySection; sections: GallerySection[]; photos: GalleryPhoto[] }>;
  },

  renameGallerySection(id: string, sectionId: string, name: string) {
    return api.tenantRequest(
      `/galleries/${encodeURIComponent(id)}/sections/${encodeURIComponent(sectionId)}`,
      { method: 'PUT', body: JSON.stringify({ name }) },
    ) as Promise<{ sections: GallerySection[] }>;
  },

  moveGalleryPhotos(id: string, photoIds: string[], sectionId: string) {
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}/photos/move`, {
      method: 'PUT',
      body: JSON.stringify({ photoIds, sectionId }),
    }) as Promise<{ photos: GalleryPhoto[] }>;
  },

  moveGallerySection(id: string, sectionId: string, direction: -1 | 1) {
    return api.tenantRequest(
      `/galleries/${encodeURIComponent(id)}/sections/${encodeURIComponent(sectionId)}`,
      { method: 'PUT', body: JSON.stringify({ direction }) },
    ) as Promise<{ sections: GallerySection[] }>;
  },

  deleteGallerySection(id: string, sectionId: string) {
    return api.tenantRequest(
      `/galleries/${encodeURIComponent(id)}/sections/${encodeURIComponent(sectionId)}`,
      { method: 'DELETE' },
    ) as Promise<{ sections: GallerySection[]; photos: GalleryPhoto[]; coverPhotoId: string | null }>;
  },

  uploadGalleryWatermark(id: string, file: File) {
    const body = new FormData();
    body.append('file', file);
    return fetch(`/api/tenant/galleries/${encodeURIComponent(id)}/watermark`, {
      method: 'POST',
      headers: tenantUploadHeaders(),
      body,
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'galleries_save_failed');
      return data as { watermarkPng: true };
    });
  },

  galleryWatermark(galleryId: string) {
    return fetch(`/api/tenant/galleries/${encodeURIComponent(galleryId)}/watermark`, {
      headers: tenantUploadHeaders(),
    }).then(async (res) => {
      if (!res.ok) throw new Error('preview_failed');
      return res.blob();
    });
  },

  deleteGalleryWatermark(id: string) {
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}/watermark`, {
      method: 'DELETE',
    }) as Promise<{ watermarkPng: false }>;
  },

  setGalleryCover(id: string, photoId: string) {
    return api.tenantRequest(`/galleries/${encodeURIComponent(id)}/cover`, {
      method: 'PUT',
      body: JSON.stringify({ photoId }),
    }) as Promise<{ coverPhotoId: string }>;
  },

  updateGalleryPhoto(id: string, photoId: string, body: { rotate?: boolean; hidden?: boolean }) {
    return api.tenantRequest(
      `/galleries/${encodeURIComponent(id)}/photos/${encodeURIComponent(photoId)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ) as Promise<{ photo: GalleryPhoto }>;
  },

  deleteGalleryPhoto(id: string, photoId: string) {
    return api.tenantRequest(
      `/galleries/${encodeURIComponent(id)}/photos/${encodeURIComponent(photoId)}`,
      { method: 'DELETE' },
    ) as Promise<{ photos: GalleryPhoto[]; coverPhotoId: string | null }>;
  },

  listGalleryTrash() {
    return api.tenantRequest('/galleries/trash') as Promise<{
      items: Array<{
        id: string;
        galleryId: string;
        originalName: string;
        deletedAt: string;
        width: number | null;
        height: number | null;
        rotation: number;
        mediaKind: GalleryPhoto['mediaKind'];
        albumName: string;
        nameI18n: { ru: string; en: string; he: string };
      }>;
      albums: Array<{
        id: string;
        name: string;
        nameI18n: { ru: string; en: string; he: string };
        coverPhotoId: string | null;
        deletedAt: string;
        photos: number;
        bytes: number;
      }>;
    }>;
  },

  restoreGalleryTrash(photoIds: string[], galleryIds: string[] = []) {
    return api.tenantRequest('/galleries/trash/restore', {
      method: 'POST',
      body: JSON.stringify({ photoIds, galleryIds }),
    }) as Promise<{ galleryIds: string[] }>;
  },

  emptyGalleryTrash() {
    return api.tenantRequest('/galleries/trash/empty', {
      method: 'POST',
      body: JSON.stringify({}),
    }) as Promise<{ removed: number }>;
  },

  galleryPhotoPreview(galleryId: string, photoId: string) {
    return fetch(
      `/api/tenant/galleries/${encodeURIComponent(galleryId)}/photos/${encodeURIComponent(photoId)}/preview`,
      { headers: tenantUploadHeaders() },
    ).then((res) => readPreviewLink(res));
  },

  listAlbums() {
    return api.tenantRequest('/albums') as Promise<{
      albums: Array<{
        id: string;
        title: string;
        description: string;
        publicCode: string;
        pageTemplate: 'grid' | 'page';
        showTitle: boolean;
        showDescription: boolean;
        pageBgColor?: string;
        itemCount: number;
        createdAt: string;
        updatedAt: string;
      }>;
    }>;
  },

  createAlbum(body: {
    title?: string;
    description?: string;
    pageTemplate?: 'page' | 'living' | 'grid';
    showTitle?: boolean;
    showDescription?: boolean;
    pageBgColor?: string;
    closingFrontTextI18n?: { ru?: string; en?: string; he?: string };
    closingBackTextI18n?: { ru?: string; en?: string; he?: string };
    includeSubcategoryTitles?: boolean;
    items: Array<{ productId?: string | null; imageUrl: string }>;
  }) {
    return api.tenantRequest('/albums', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ album: unknown }>;
  },

  deleteAlbum(id: string) {
    return api.tenantRequest(`/albums/${id}`, { method: 'DELETE' }) as Promise<{ ok: boolean }>;
  },

  updateTenantDigitalAlbumSettings(body: { zipDownloadEnabled: boolean }) {
    return api.tenantRequest('/settings/digital-album', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  getTenantVisitingCard(lang?: string) {
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    return api.tenantRequest(`/visiting-card${qs}`) as Promise<{ card: VisitingCardPayload }>;
  },

  updateTenantVisitingCard(body: Record<string, unknown>, lang?: string) {
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    return api.tenantRequest(`/visiting-card${qs}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ card: VisitingCardPayload; config: VisitingCardConfig }>;
  },

  async getPublicVisitingCard(lang?: string) {
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    const res = await fetch(`/api/public/visiting-card${qs}`, {
      headers: storefrontHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'visiting_card_failed'), {
        data,
        status: res.status,
      });
    }
    return data as { card: VisitingCardPayload };
  },

  /** Uncapped catalog photo list for the visiting-card fullscreen viewer. */
  async getPublicVisitingCardGallery(lang?: string) {
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    const res = await fetch(`/api/public/visiting-card/gallery${qs}`, {
      headers: storefrontHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'visiting_card_gallery_failed'), {
        data,
        status: res.status,
      });
    }
    return data as {
      slides: Array<{
        id: string;
        productId: string;
        imageUrl: string;
        title: string;
        description: string;
      }>;
    };
  },

  async getPublicAlbum(code: string) {
    const res = await fetch(`/api/public/albums/${encodeURIComponent(code)}`, {
      headers: storefrontHeaders(),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'album_failed'), { data, status: res.status });
    }
    return data as {
      album: {
        title: string;
        description: string;
        publicCode: string;
        createdAt: string;
        pageTemplate?: 'grid' | 'page';
        showTitle: boolean;
        showDescription: boolean;
        pageBgColor?: string;
        items: Array<{
          imageUrl: string;
          productId?: string | null;
          sku?: string;
          nameI18n?: Partial<Record<'ru' | 'en' | 'he', string>>;
          descriptionI18n?: Partial<Record<'ru' | 'en' | 'he', string>>;
          thumbnailUrl?: string | null;
          price?: number | null;
          basePrice?: number | null;
          promoPrice?: number | null;
          availabilityStatus?: string | null;
          quantity?: number | null;
        }>;
        images?: string[];
        storefront: {
          slug: string;
          logoUrl?: string | null;
          landingI18n?: unknown;
          allowGuestCart?: boolean;
          allowCartWithoutPrice?: boolean;
        };
      };
    };
  },

  listCatalogTerms(query: { kind: 'tag' | 'style'; q?: string }) {
    const params = new URLSearchParams({ kind: query.kind });
    if (query.q) params.set('q', query.q);
    return api.tenantRequest(`/catalog-terms?${params.toString()}`) as Promise<{
      terms: import('../types').CatalogTerm[];
    }>;
  },

  matchCatalogTerm(body: { kind: 'tag' | 'style'; text: string }) {
    return api.tenantRequest('/catalog-terms/match', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ term: import('../types').CatalogTerm | null }>;
  },

  analyzeProductImage(body: { imageUrl: string; categoryName?: string }) {
    return api.tenantRequest('/products/analyze-image', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{
      nameEn: string;
      descriptionEn: string;
      tags: import('../types').CatalogTerm[];
      styles: import('../types').CatalogTerm[];
    }>;
  },

  findOrCreateCatalogTerm(body: { kind: 'tag' | 'style'; text: string; sourceLang: Language }) {
    return api.tenantRequest('/catalog-terms', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{
      term: import('../types').CatalogTerm;
      created: boolean;
      translated: boolean;
    }>;
  },

  listTenantProducts(search?: string) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return api.tenantRequest(`/products${qs}`) as Promise<{
      products: Product[];
      productCount?: number;
      maxProducts?: number;
    }>;
  },

  getTenantProduct(id: string) {
    return api.tenantRequest(`/products/${id}`) as Promise<{ product: Product }>;
  },

  getTenantProductBySku(sku: string) {
    const params = new URLSearchParams();
    params.set('exactSku', sku);
    return api.tenantRequest(`/products?${params.toString()}`) as Promise<{ products: Product[] }>;
  },

  /** Soft stub — CoW has no brands module; Al Hilal sync catches empty list. */
  listTenantBrands() {
    return Promise.resolve({ brands: [] as Array<{ id: string; nameI18n?: Record<string, string> }> });
  },

  scrapeTenantProductUrl(url: string) {
    return api.tenantRequest('/products/scrape-url', {
      method: 'POST',
      body: JSON.stringify({ url }),
    }) as Promise<{
      draft: {
        sourceUrl: string;
        sku: string;
        title: string;
        description: string;
        descriptionEn?: string;
        imageUrls: string[];
        price: { amount: number; currency: string | null; formatted: string | null } | null;
        categoryHint: string | null;
        brandHint?: string | null;
        specs: Record<string, string>;
        variantConfig?: Product['variantConfig'];
      };
    }>;
  },

  previewPartnerWhitelist(
    skus: string | string[],
    options: { mode?: 'full' | 'add_only' } = {},
  ) {
    return api.tenantRequest('/products/partner-whitelist/preview', {
      method: 'POST',
      body: JSON.stringify({ skus, mode: options.mode || 'full' }),
    }) as Promise<{
      mode?: 'full' | 'add_only';
      whitelistCount: number;
      dbProductCount: number;
      alreadyVisibleCount: number;
      willActivate: Array<{ id: string; sku: string; active: boolean }>;
      willHide: Array<{ id: string; sku: string; active: boolean }>;
      willRefresh: Array<{ id: string; sku: string; active: boolean }>;
      missing: string[];
      partnerAuthConfigured?: boolean;
    }>;
  },

  applyPartnerWhitelistVisibility(
    skus: string | string[],
    options: { mode?: 'full' | 'add_only' } = {},
  ) {
    return api.tenantRequest('/products/partner-whitelist/apply-visibility', {
      method: 'POST',
      body: JSON.stringify({ skus, mode: options.mode || 'full' }),
    }) as Promise<{
      mode?: 'full' | 'add_only';
      hiddenCount: number;
      activatedCount: number;
      hidden: Array<{ id: string; sku: string }>;
      activated: Array<{ id: string; sku: string }>;
    }>;
  },

  refreshPartnerProductPrice(sku: string) {
    return api.tenantRequest('/products/partner-refresh-price', {
      method: 'POST',
      body: JSON.stringify({ sku }),
    }) as Promise<{
      sku: string;
      oldPrice: number | null;
      newPrice: number | null;
      unchanged: boolean;
      variantMatched: number;
      variantNote?: string | null;
      oldVariantPrices?: Array<number | null>;
      newVariantPrices?: Array<number | null>;
    }>;
  },

  createTenantProduct(body: {
    sku: string;
    nameI18n: Record<string, string>;
    descriptionI18n?: Record<string, string>;
    categoryId?: string;
    subcategoryId?: string;
    brandId?: string | null;
    youtubeUrl?: string | null;
    imageUrls?: string[];
    mockupLibraryIds?: string[];
    mockupPickMode?: 'manual' | 'random';
    mockupFolderPath?: string;
    discardedImageUrls?: string[];
    galleryOrderLocked?: boolean;
    attributeValues?: Array<{ definitionId: string; value: string | number | boolean }>;
    catalogTermIds?: string[];
    catalogTerms?: Array<{ id?: string | null; kind: 'tag' | 'style'; labelI18n: Record<string, string> }>;
    active?: boolean;
    variantConfig?: unknown;
    variantTemplateId?: string | null;
    partnerImport?: {
      sourceUrl: string;
      title?: string;
      description?: string;
      imageUrls?: string[];
      price?: { amount: number; currency: string | null; formatted: string | null } | null;
      categoryHint?: string | null;
      specs?: Record<string, string>;
      variantConfig?: unknown;
    };
  }) {
    return api.tenantRequest('/products', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  createTenantProductsFromImport(body: {
    fileNames: string[];
    baseName: string;
    language: Language;
    categoryId: string;
  }) {
    return api.tenantRequest('/products/batch-from-import', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ products: Product[] }>;
  },

  moveTenantProductsToCategory(body: { productIds: string[]; categoryId: string }) {
    return api.tenantRequest('/products/batch-category', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ ok: boolean; movedCount: number; categoryId: string }>;
  },

  updateTenantProduct(
    id: string,
    body: {
      sku: string;
      nameI18n: Record<string, string>;
      descriptionI18n?: Record<string, string>;
      categoryId?: string;
      subcategoryId?: string;
      brandId?: string | null;
      youtubeUrl?: string | null;
      imageUrls?: string[];
      mockupLibraryIds?: string[];
      mockupPickMode?: 'manual' | 'random';
      mockupFolderPath?: string;
      discardedImageUrls?: string[];
      galleryOrderLocked?: boolean;
      attributeValues?: Array<{ definitionId: string; value: string | number | boolean }>;
      catalogTermIds?: string[];
      catalogTerms?: Array<{ id?: string | null; kind: 'tag' | 'style'; labelI18n: Record<string, string> }>;
      active?: boolean;
      variantConfig?: unknown;
      variantTemplateId?: string | null;
      partnerImport?: {
        sourceUrl: string;
        title?: string;
        description?: string;
        imageUrls?: string[];
        price?: { amount: number; currency: string | null; formatted: string | null } | null;
        categoryHint?: string | null;
        specs?: Record<string, string>;
        variantConfig?: unknown;
      };
    },
  ) {
    return api.tenantRequest(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  listVariantTemplates() {
    return api.tenantRequest('/variant-templates') as Promise<{
      templates: VariantTemplate[];
    }>;
  },

  createVariantTemplate(body: {
    key?: string;
    nameI18n?: Record<string, string>;
    config?: unknown;
    active?: boolean;
  }) {
    return api.tenantRequest('/variant-templates', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ template: VariantTemplate }>;
  },

  updateVariantTemplate(
    id: string,
    body: {
      key?: string;
      nameI18n?: Record<string, string>;
      config?: unknown;
      active?: boolean;
    },
  ) {
    return api.tenantRequest(`/variant-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ template: VariantTemplate }>;
  },

  deleteVariantTemplate(id: string) {
    return api.tenantRequest(`/variant-templates/${id}`, { method: 'DELETE' }) as Promise<{ ok: boolean }>;
  },

  listAddonTemplates() {
    return api.tenantRequest('/addon-templates') as Promise<{
      templates: AddonTemplate[];
    }>;
  },

  createAddonTemplate(body: {
    key?: string;
    nameI18n?: Record<string, string>;
    config?: unknown;
    active?: boolean;
  }) {
    return api.tenantRequest('/addon-templates', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ template: AddonTemplate }>;
  },

  updateAddonTemplate(
    id: string,
    body: {
      key?: string;
      nameI18n?: Record<string, string>;
      config?: unknown;
      active?: boolean;
    },
  ) {
    return api.tenantRequest(`/addon-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ template: AddonTemplate }>;
  },

  deleteAddonTemplate(id: string) {
    return api.tenantRequest(`/addon-templates/${id}`, { method: 'DELETE' }) as Promise<{ ok: boolean }>;
  },

  deleteTenantProduct(id: string) {
    return api.tenantRequest(`/products/${id}`, { method: 'DELETE' });
  },

  listTenantPrices(filters?: { search?: string; priceMin?: number; priceMax?: number }) {
    const params = new URLSearchParams();
    if (filters?.search) params.set('search', filters.search);
    if (filters?.priceMin != null) params.set('priceMin', String(filters.priceMin));
    if (filters?.priceMax != null) params.set('priceMax', String(filters.priceMax));
    const qs = params.toString();
    return api.tenantRequest(`/prices${qs ? `?${qs}` : ''}`) as Promise<{ items: ProductPriceItem[] }>;
  },

  updateTenantProductPrice(
    productId: string,
    price: number | null,
    variantConfig?: ProductPriceItem['variantConfig'],
  ) {
    return api.tenantRequest(`/products/${productId}/price`, {
      method: 'PUT',
      body: JSON.stringify({
        price,
        ...(variantConfig !== undefined ? { variantConfig } : {}),
      }),
    }) as Promise<{ item: ProductPriceItem }>;
  },

  listTenantStock(search?: string) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return api.tenantRequest(`/stock${qs}`) as Promise<{
      items: ProductStockItem[];
      defaultLowStockThreshold: number;
    }>;
  },

  updateTenantProductStock(
    productId: string,
    body: { quantity: number; lowStockThreshold: number | null },
  ) {
    return api.tenantRequest(`/products/${productId}/stock`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ item: ProductStockItem; defaultLowStockThreshold: number }>;
  },

  listTenantStockAlerts(resolved = false) {
    const qs = resolved ? '?resolved=1' : '';
    return api.tenantRequest(`/stock-alerts${qs}`) as Promise<{ items: StockAlertItem[] }>;
  },

  resolveTenantStockAlert(id: string) {
    return api.tenantRequest(`/stock-alerts/${id}/resolve`, {
      method: 'PATCH',
    }) as Promise<{ ok: boolean; items: StockAlertItem[] }>;
  },

  listTenantClients(search?: string) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return api.tenantRequest(`/clients${qs}`) as Promise<{ clients: Client[] }>;
  },

  createTenantClient(body: {
    email: string;
    name: string;
    phone?: string | null;
    discountPercent?: number | null;
    active?: boolean;
  }) {
    return api.tenantRequest('/clients', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ client: Client }>;
  },

  updateTenantClient(
    id: string,
    body: {
      name: string;
      phone?: string | null;
      discountPercent?: number | null;
      active?: boolean;
    },
  ) {
    return api.tenantRequest(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ client: Client }>;
  },

  deleteTenantClient(id: string) {
    return api.tenantRequest(`/clients/${id}`, {
      method: 'DELETE',
    }) as Promise<{ ok: boolean }>;
  },

  listTenantRegistrationRequests(status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending') {
    const qs = status === 'pending' ? '' : `?status=${encodeURIComponent(status)}`;
    return api.tenantRequest(`/registration-requests${qs}`) as Promise<{
      requests: RegistrationRequest[];
      pendingCount: number;
    }>;
  },

  approveTenantRegistrationRequest(id: string, body?: { discountPercent?: number | null }) {
    return api.tenantRequest(`/registration-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }) as Promise<{ client: Client }>;
  },

  completeTenantRegistrationRequest(id: string) {
    return api.tenantRequest(`/registration-requests/${id}/complete`, {
      method: 'POST',
    }) as Promise<{ ok: boolean }>;
  },

  rejectTenantRegistrationRequest(id: string) {
    return api.tenantRequest(`/registration-requests/${id}/reject`, {
      method: 'POST',
    }) as Promise<{ ok: boolean }>;
  },

  getTenantSettings() {
    return api.tenantRequest('/settings') as Promise<{ settings: TenantSettings }>;
  },

  getNotifyLang() {
    return api.tenantRequest('/settings/notify-lang') as Promise<{ lang: 'en' | 'ru' | 'he' }>;
  },

  saveNotifyLang(lang: 'en' | 'ru' | 'he') {
    return api.tenantRequest('/settings/notify-lang', {
      method: 'PUT',
      body: JSON.stringify({ lang }),
    }) as Promise<{ lang: 'en' | 'ru' | 'he' }>;
  },

  runTenantA11yPublicScan() {
    return api.tenantRequest('/a11y/scan', {
      method: 'POST',
    }) as Promise<{
      ok: true;
      settings: TenantSettings;
      summary?: TenantSettings['a11yLastPublicScanSummary'];
    }>;
  },

  exportTenantUiPreset() {
    return api.tenantRequest('/settings/ui-preset/export') as Promise<{ preset: Record<string, unknown> }>;
  },

  applyTenantUiPreset(body: { preset: unknown; label?: string; sourceRef?: string }) {
    return api.tenantRequest('/settings/ui-preset/apply', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{
      applied: string[];
      snapshotId: string | null;
      settings: TenantSettings;
    }>;
  },

  listTenantUiSnapshots() {
    return api.tenantRequest('/settings/ui-preset/snapshots') as Promise<{
      snapshots: import('./uiPreset').UiSnapshotSummary[];
    }>;
  },

  listTenantUiPresets() {
    return api.tenantRequest('/settings/ui-presets') as Promise<{
      presets: import('./uiPreset').UiPresetSummary[];
    }>;
  },

  createTenantUiPreset(body: { name: string; labelI18n?: Record<string, string> }) {
    return api.tenantRequest('/settings/ui-presets', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ preset: import('./uiPreset').UiPresetSummary }>;
  },

  deleteTenantUiPreset(id: string) {
    return api.tenantRequest(`/settings/ui-presets/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }) as Promise<{ ok: boolean }>;
  },

  applyTenantUiPresetById(id: string) {
    return api.tenantRequest(`/settings/ui-presets/${encodeURIComponent(id)}/apply`, {
      method: 'POST',
    }) as Promise<{
      applied: string[];
      snapshotId: string | null;
      settings: TenantSettings;
    }>;
  },

  listPlatformUiPresets() {
    return api.platformRequest('/ui-presets') as Promise<{
      presets: import('./uiPreset').UiPresetSummary[];
    }>;
  },

  uploadPlatformUiPresetCover(id: string, file: File) {
    const body = new FormData();
    body.append('file', file);
    return api.platformRequest(`/ui-presets/${encodeURIComponent(id)}/cover`, {
      method: 'POST',
      body,
    }) as Promise<{ preset: import('./uiPreset').UiPresetSummary }>;
  },

  setPlatformUiPresetActive(id: string, active: boolean) {
    return api.platformRequest(`/ui-presets/${encodeURIComponent(id)}/active`, {
      method: 'PUT',
      body: JSON.stringify({ active }),
    }) as Promise<{ preset: import('./uiPreset').UiPresetSummary }>;
  },

  deletePlatformUiPreset(id: string) {
    return api.platformRequest(`/ui-presets/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }) as Promise<{ ok: boolean }>;
  },

  saveTenantUiSnapshot(body?: { label?: string }) {
    return api.tenantRequest('/settings/ui-preset/snapshots', {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }) as Promise<{ snapshot: import('./uiPreset').UiSnapshotSummary }>;
  },

  restoreTenantUiSnapshot(id: string) {
    return api.tenantRequest(`/settings/ui-preset/snapshots/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
    }) as Promise<{
      applied: string[];
      restoredFrom: string;
      snapshotId: string | null;
      settings: TenantSettings;
    }>;
  },

  listUiCloneDonors() {
    return api.tenantRequest('/settings/ui-preset/clone-donors') as Promise<{
      allowed: boolean;
      donors: import('./uiPreset').UiCloneDonorSummary[];
    }>;
  },

  cloneTenantUiFromDonor(donorTenantId: string, label?: string) {
    return api.tenantRequest('/settings/ui-preset/clone-from', {
      method: 'POST',
      body: JSON.stringify({ donorTenantId, label }),
    }) as Promise<{
      applied: string[];
      snapshotId: string | null;
      donor: import('./uiPreset').UiCloneDonorSummary;
      settings: TenantSettings;
    }>;
  },

  updateTenantLandingSettings(body: {
    landing: TenantSettings['landing'];
    logoUrl?: string | null;
    storefront?: TenantSettings['storefront'];
  }) {
    return api.tenantRequest('/settings/landing', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantDesignSettings(body: {
    catalogDesktopLayout: TenantSettings['landing']['catalogDesktopLayout'];
    catalogGrid: TenantSettings['landing']['catalogGrid'];
    catalogCard: TenantSettings['landing']['catalogCard'];
    headerTitleMode: TenantSettings['landing']['headerTitleMode'];
    showHomeButton: boolean;
  }) {
    return api.tenantRequest('/settings/design', {
      method: 'PUT',
      body: JSON.stringify({ landing: body }),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantStorefrontSettings(body: {
    storefront:
      | TenantSettings['storefront']
      | {
        homeEnabled?: boolean;
        listOnPlatform?: boolean;
        allowSearchIndexing?: boolean;
      };
  }) {
    return api.tenantRequest('/settings/storefront', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantPlatformCardSettings(body: {
    platformCard: TenantSettings['platformCard'];
    storefront?: {
      listOnPlatform?: boolean;
      allowSearchIndexing?: boolean;
    };
  }) {
    return api.tenantRequest('/settings/storefront', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantThemeSettings(body: { theme: TenantSettings['theme'] }) {
    return api.tenantRequest('/settings/theme', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantClientsSettings(body: { clients: TenantSettings['clients'] }) {
    return api.tenantRequest('/settings/clients', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantStockSettings(body: { stock: TenantSettings['stock'] }) {
    return api.tenantRequest('/settings/stock', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantCatalogSettings(body: { catalog: TenantSettings['catalog'] }) {
    return api.tenantRequest('/settings/catalog', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantOrdersSettings(body: { orders: TenantSettings['orders'] }) {
    return api.tenantRequest('/settings/orders', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantSecuritySettings(body: { security: TenantSettings['security'] }) {
    return api.tenantRequest('/settings/security', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantLegalDocuments(body: { legalDocuments: TenantSettings['legalDocuments'] }) {
    return api.tenantRequest('/settings/legal', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantBiographySettings(body: {
    i18n: TenantSettings['biography']['i18n'];
    photoUrls: string[];
    layout?: TenantSettings['biography']['layout'];
    showOnStorefront?: boolean;
    contentTag?: string | null;
  }) {
    return api.tenantRequest('/settings/biography', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  listTenantContentTags() {
    return api.tenantRequest('/content-tags') as Promise<{
      tags: Array<{
        tag: string;
        objectType: string;
        objectId: string;
        preview: string;
        createdAt: string;
      }>;
    }>;
  },

  updateTenantLivingAlbumSettings(body: TenantSettings['livingAlbum']) {
    return api.tenantRequest('/settings/living-album', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  updateTenantCabinetSettings(body: { navLayout: TenantSettings['navLayout'] }) {
    return api.tenantRequest('/settings/cabinet', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ settings: TenantSettings }>;
  },

  getTenantCabinetSettings() {
    return api.tenantRequest('/settings/cabinet') as Promise<{
      navLayout: TenantSettings['navLayout'];
    }>;
  },

  getTenantTelegramStatus() {
    return api.tenantRequest('/settings/telegram') as Promise<TelegramLinkStatus>;
  },

  createTenantTelegramLinkToken() {
    return api.tenantRequest('/settings/telegram/link-token', {
      method: 'POST',
    }) as Promise<TelegramLinkTokenResponse>;
  },

  revokeTenantTelegramLink() {
    return api.tenantRequest('/settings/telegram/link', { method: 'DELETE' }) as Promise<{ ok: boolean }>;
  },

  updateTenantTelegramCatalog(enabled: boolean) {
    return api.tenantRequest('/settings/telegram/catalog', {
      method: 'PUT',
      body: JSON.stringify({ catalogEnabled: enabled }),
    }) as Promise<{ catalogEnabled: boolean }>;
  },

  getPlatformTelegramStatus() {
    return api.platformRequest('/telegram') as Promise<TelegramLinkStatus>;
  },

  createPlatformTelegramLinkToken() {
    return api.platformRequest('/telegram/link-token', {
      method: 'POST',
    }) as Promise<TelegramLinkTokenResponse>;
  },

  revokePlatformTelegramLink() {
    return api.platformRequest('/telegram/link', { method: 'DELETE' }) as Promise<{ ok: boolean }>;
  },

  setPlatformTelegramSubscription(tenantId: string, subscribe: boolean) {
    return api.platformRequest(`/telegram/subscriptions/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify({ subscribe }),
    }) as Promise<TelegramLinkStatus>;
  },

  listTenantPromotions() {
    return api.tenantRequest('/promotions') as Promise<{ promotions: Promotion[] }>;
  },

  getTenantPromotion(id: string) {
    return api.tenantRequest(`/promotions/${id}`) as Promise<{
      promotion: Promotion;
      products: PromotionProductItem[];
    }>;
  },

  createTenantPromotion(body: {
    nameI18n: Record<string, string>;
    startsAt: string;
    endsAt: string;
    active?: boolean;
    scopeType?: 'all' | 'categories' | 'manual';
    discountPercent?: number | null;
    categoryIds?: string[];
  }) {
    return api.tenantRequest('/promotions', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ promotion: Promotion }>;
  },

  updateTenantPromotion(
    id: string,
    body: {
      nameI18n: Record<string, string>;
      startsAt: string;
      endsAt: string;
      active?: boolean;
      scopeType?: 'all' | 'categories' | 'manual';
      discountPercent?: number | null;
      categoryIds?: string[];
    },
  ) {
    return api.tenantRequest(`/promotions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ promotion: Promotion }>;
  },

  deleteTenantPromotion(id: string) {
    return api.tenantRequest(`/promotions/${id}`, { method: 'DELETE' });
  },

  previewPromotionScope(body: {
    scopeType: 'all' | 'categories' | 'manual';
    discountPercent?: number | null;
    categoryIds?: string[];
  }) {
    return api.tenantRequest('/promotions/scope-preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ count: number }>;
  },

  upsertPromotionProduct(promotionId: string, productId: string, promoPrice: number) {
    return api.tenantRequest(`/promotions/${promotionId}/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify({ promoPrice }),
    }) as Promise<{ item: PromotionProductItem; products: PromotionProductItem[] }>;
  },

  removePromotionProduct(promotionId: string, productId: string) {
    return api.tenantRequest(`/promotions/${promotionId}/products/${productId}`, {
      method: 'DELETE',
    }) as Promise<{ products: PromotionProductItem[] }>;
  },

  listTenantCategories() {
    return api.tenantRequest('/categories') as Promise<{ categories: Category[] }>;
  },

  listTenantRoomMockups() {
    return api.tenantRequest('/room-mockups') as Promise<{
      mockups: import('./roomMockup').RoomMockup[];
    }>;
  },

  saveTenantRoomMockups(body: {
    mockups: Array<{
      id?: string;
      url: string;
      corners: Array<{ x: number; y: number }>;
    }>;
  }) {
    return api.tenantRequest('/room-mockups', {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{
      mockups: import('./roomMockup').RoomMockup[];
    }>;
  },

  createTenantCategory(body: {
    nameI18n: Record<string, string>;
    parentId?: string | null;
    imageUrl?: string | null;
    optionSetValues?: Record<string, string[]>;
  }) {
    return api.tenantRequest('/categories', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ category: Category }>;
  },

  updateTenantCategory(
    id: string,
    body: {
      nameI18n: Record<string, string>;
      parentId?: string | null;
      imageUrl?: string | null;
      optionSetValues?: Record<string, string[]>;
    },
  ) {
    return api.tenantRequest(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ category: Category }>;
  },

  deleteTenantCategory(id: string) {
    return api.tenantRequest(`/categories/${id}`, { method: 'DELETE' });
  },

  listCategoryAttributeDefinitions(categoryId: string, options?: { inherit?: boolean }) {
    const qs = options?.inherit ? '?inherit=1' : '';
    return api.tenantRequest(
      `/categories/${encodeURIComponent(categoryId)}/attribute-definitions${qs}`,
    ) as Promise<{ definitions: AttributeDefinition[] }>;
  },

  listAttributeDefinitionCounts() {
    return api.tenantRequest('/attribute-definition-counts') as Promise<{
      counts: Record<string, number>;
    }>;
  },

  createCategoryAttributeDefinition(
    categoryId: string,
    body: {
      key: string;
      nameI18n: Record<string, string>;
      valueType: AttributeDefinition['valueType'];
      enumOptions?: string[];
      unit?: string | null;
      filterable?: boolean;
    },
  ) {
    return api.tenantRequest(
      `/categories/${encodeURIComponent(categoryId)}/attribute-definitions`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ) as Promise<{ definition: AttributeDefinition }>;
  },

  updateCategoryAttributeDefinition(
    id: string,
    body: {
      key: string;
      nameI18n: Record<string, string>;
      valueType: AttributeDefinition['valueType'];
      enumOptions?: string[];
      unit?: string | null;
      filterable?: boolean;
    },
  ) {
    return api.tenantRequest(`/attribute-definitions/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<{ definition: AttributeDefinition }>;
  },

  patchCategoryAttributeDefinitionFilterable(id: string, filterable: boolean) {
    return api.tenantRequest(`/attribute-definitions/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ filterable }),
    }) as Promise<{ definition: AttributeDefinition }>;
  },

  deleteCategoryAttributeDefinition(id: string) {
    return api.tenantRequest(`/attribute-definitions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  translateTenantFields(body: { fields: Record<string, string>; context?: string }) {
    return api.tenantRequest('/translate', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ fields: { ru: string; en: string; he: string } }>;
  },

  translateTenantBlocks(body: {
    blocks: Array<{
      id: string;
      fields: Record<string, string>;
      context?: string;
      /** Prefer this locale as translation source when non-empty. */
      sourceLang?: string;
    }>;
  }) {
    return api.tenantRequest('/translate', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{
      blocks: Record<string, { ru: string; en: string; he: string }>;
    }>;
  },

  async uploadTenantProductMedia(file: File) {
    const form = new FormData();
    form.append('file', file);

    const res = await fetch('/api/tenant/uploads/products', {
      method: 'POST',
      headers: tenantUploadHeaders(),
      body: form,
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'upload_failed'), { data, status: res.status });
    }
    return data as { url: string; kind: 'image' | 'pdf' };
  },

  deleteTenantProductMediaUrls(urls: string[]) {
    return api.tenantRequest('/uploads/products/delete', {
      method: 'POST',
      body: JSON.stringify({ urls }),
    }) as Promise<{ ok: boolean; deleted: number; urls: string[] }>;
  },

  async fetchTenantImportMediaPreview(fileName: string) {
    const token = getStoredToken();
    const res = await fetch(
      `/api/tenant/import-media/file/${encodeURIComponent(fileName)}`,
      {
        headers: {
          ...tenantScopeHeaders(),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );
    if (!res.ok) {
      throw Object.assign(new Error('import_media_preview_failed'), { status: res.status });
    }
    return res.blob();
  },

  async getTenantImportMedia(params?: { limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params?.limit != null) query.set('limit', String(params.limit));
    if (params?.offset != null) query.set('offset', String(params.offset));
    const suffix = query.toString() ? `?${query.toString()}` : '';

    return api.tenantRequest(`/import-media${suffix}`) as Promise<{
      items: Array<{
        fileName: string;
        mediaId: string | null;
        stem: string | null;
        sizeBytes: number;
        modifiedAt: string;
        previewPath: string;
      }>;
      total: number;
    }>;
  },

  adoptTenantImportMedia(fileName: string) {
    return api.tenantRequest('/import-media/adopt', {
      method: 'POST',
      body: JSON.stringify({ fileName }),
    }) as Promise<{
      url: string;
      fileName: string;
      mediaId: string | null;
      stem: string | null;
    }>;
  },

  /** Upload one image into import staging with progress (XHR, fetch has no upload progress). */
  uploadTenantImportMedia(
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<{
    item: {
      fileName: string;
      mediaId: string | null;
      stem: string | null;
      sizeBytes: number;
      modifiedAt: string;
      previewPath: string;
    };
  }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/tenant/import-media/upload');

      const headers = tenantUploadHeaders() as Record<string, string>;
      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value);
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        let data: unknown = null;
        try {
          data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch {
          data = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data as { item: never });
        } else {
          const error = (data as { error?: string })?.error || 'import_media_upload_failed';
          reject(Object.assign(new Error(error), { data, status: xhr.status }));
        }
      };

      xhr.onerror = () => {
        reject(Object.assign(new Error('import_media_upload_failed'), { status: xhr.status }));
      };

      const form = new FormData();
      form.append('file', file);
      xhr.send(form);
    });
  },

  async uploadTenantCategoryImage(file: File, token: string) {
    const form = new FormData();
    form.append('file', file);
    form.append('token', token);

    const res = await fetch('/api/tenant/uploads/categories', {
      method: 'POST',
      headers: tenantUploadHeaders(),
      body: form,
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'upload_failed'), { data, status: res.status });
    }
    return data as { url: string };
  },

  async uploadTenantOptionSetItemImage(file: File, token: string) {
    const form = new FormData();
    form.append('file', file);
    form.append('token', token);

    const res = await fetch('/api/tenant/uploads/option-set-items', {
      method: 'POST',
      headers: tenantUploadHeaders(),
      body: form,
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'upload_failed'), { data, status: res.status });
    }
    return data as { url: string };
  },

  async copyTenantCategoryImageFromUrl(sourceUrl: string, token: string) {
    return api.tenantRequest('/uploads/categories/from-url', {
      method: 'POST',
      body: JSON.stringify({ sourceUrl, token }),
    }) as Promise<{ url: string }>;
  },

  async uploadTenantAnnouncementImage(file: File, token: string) {
    const form = new FormData();
    form.append('file', file);
    form.append('token', token);

    const res = await fetch('/api/tenant/uploads/announcements', {
      method: 'POST',
      headers: tenantUploadHeaders(),
      body: form,
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'upload_failed'), { data, status: res.status });
    }
    return data as { url: string };
  },

  async uploadTenantLandingLogo(file: File) {
    const form = new FormData();
    form.append('file', file);

    const res = await fetch('/api/tenant/uploads/landing', {
      method: 'POST',
      headers: tenantUploadHeaders(),
      body: form,
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'upload_failed'), { data, status: res.status });
    }
    return data as { url: string };
  },

  async uploadTenantShopSlide(file: File) {
    const form = new FormData();
    form.append('file', file);

    const res = await fetch('/api/tenant/uploads/slides', {
      method: 'POST',
      headers: tenantUploadHeaders(),
      body: form,
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'upload_failed'), { data, status: res.status });
    }
    return data as { url: string };
  },

  async uploadBiographyPhoto(file: File) {
    const form = new FormData();
    form.append('file', file);

    const res = await fetch('/api/tenant/uploads/biography', {
      method: 'POST',
      headers: tenantUploadHeaders(),
      body: form,
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'upload_failed'), { data, status: res.status });
    }
    return data as { url: string };
  },

  async uploadTenantNoteImage(file: File) {
    const form = new FormData();
    form.append('file', file);

    const res = await fetch('/api/tenant/uploads/note-images', {
      method: 'POST',
      headers: tenantUploadHeaders(),
      body: form,
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'upload_failed'), { data, status: res.status });
    }
    return data as { url: string };
  },

  async uploadTenantPlatformCardVisual(file: File) {
    return api.uploadTenantLandingLogo(file);
  },

  async copyTenantLandingImageFromUrl(sourceUrl: string) {
    return api.tenantRequest('/uploads/landing/from-url', {
      method: 'POST',
      body: JSON.stringify({ sourceUrl }),
    }) as Promise<{ url: string }>;
  },

  listTenantStaff() {
    return api.tenantRequest('/staff') as Promise<{
      staff: TenantStaffMember[];
      platformGrants: TenantStaffMember[];
    }>;
  },

  createTenantStaffMember(body: {
    email: string;
    role: TenantStaffRole;
    permissions?: Record<string, boolean> | null;
  }) {
    return api.tenantRequest('/staff', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ member: TenantStaffMember }>;
  },

  updateTenantStaffMember(
    id: string,
    body: {
      role?: TenantStaffRole;
      active?: boolean;
      permissions?: Record<string, boolean> | null;
    },
  ) {
    return api.tenantRequest(`/staff/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }) as Promise<{ member: TenantStaffMember }>;
  },

  getImportTemplateUrl(mode: ImportMode) {
    return `/api/tenant/import/templates/${mode}`;
  },

  previewTenantImport(mode: ImportMode, csv?: string) {
    return api.tenantRequest(`/import/${mode}/preview`, {
      method: 'POST',
      body: JSON.stringify(mode === 'translate' ? {} : { csv: csv ?? '' }),
    }) as Promise<ImportPreviewResult>;
  },

  previewHolylandArchive(
    file: File,
    options?: { ignoreSections?: boolean },
    onStep?: (step: HolylandArchiveStep) => void,
  ) {
    return runHolylandArchiveJob('/import/archive/preview', file, {
      ignoreSections: options?.ignoreSections === true,
    }, onStep).then((row) => {
      if (!row?.preview) throw Object.assign(new Error('import_preview_failed'), { data: { error: 'import_preview_failed' } });
      return row.preview;
    });
  },

  applyHolylandArchive(
    options?: { autoTranslate?: boolean; ignoreSections?: boolean },
    onStep?: (step: HolylandArchiveStep) => void,
  ) {
    return readHolylandArchiveStream('/import/archive/apply', {
      method: 'POST',
      headers: { ...authHeaders(), ...tenantScopeHeaders() },
      body: JSON.stringify({
        autoTranslate: options?.autoTranslate === true,
        ignoreSections: options?.ignoreSections === true,
      }),
    }, onStep).then((row) => {
      if (!row?.result) throw Object.assign(new Error('import_apply_failed'), { data: { error: 'import_apply_failed' } });
      return { ok: true as const, result: row.result };
    });
  },

  applyTenantImport(mode: ImportMode, options?: { csv?: string; autoTranslate?: boolean }) {
    return api.tenantRequest(`/import/${mode}/apply`, {
      method: 'POST',
      body: JSON.stringify({
        csv: options?.csv ?? '',
        autoTranslate: options?.autoTranslate === true,
      }),
    }) as Promise<{ ok: true; result: ImportApplyResult }>;
  },

  async downloadTenantImportTemplate(mode: ImportMode) {
    const res = await fetch(`/api/tenant/import/templates/${mode}`, {
      headers: { ...authHeaders(), ...tenantScopeHeaders() },
    });
    if (!res.ok) {
      const data = await parseJson(res);
      throw Object.assign(new Error(data.error || 'download_failed'), { data, status: res.status });
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `catalogofworks-import-${mode}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  resetTenantCatalog(confirmSlug: string) {
    return api.tenantRequest('/maintenance/reset-catalog', {
      method: 'POST',
      body: JSON.stringify({ confirmSlug }),
    }) as Promise<{
      ok: true;
      result: { deletedProducts: number; deletedCategories: number; deletedPromotions: number };
    }>;
  },

  fetchVariantLabelCollisions() {
    return api.tenantRequest('/maintenance/variant-label-collisions') as Promise<{
      ok: true;
      groupCount: number;
      groups: Array<{
        labelKey: string;
        labelI18n: Record<string, string>;
        axisCount: number;
        valueKeyCount: number;
        valueKeys: string[];
        entries: Array<{
          axisKey: string;
          valueKey: string;
          axisLabelI18n: Record<string, string>;
          valueLabelI18n: Record<string, string>;
          productCount: number;
          sampleSkus: string[];
        }>;
      }>;
    }>;
  },

  fetchVariantAxes() {
    return api.tenantRequest('/maintenance/variant-axes') as Promise<{
      ok: true;
      axes: Array<{
        axisKey: string;
        axisLabelI18n: Record<string, string>;
        productCount: number;
      }>;
    }>;
  },

  moveVariantValue(body: {
    dryRun: boolean;
    confirmSlug?: string;
    fromAxisKey: string;
    fromValueKey: string;
    toAxisKey: string;
    toValueKey?: string;
    toAxisLabelI18n?: Record<string, string>;
  }) {
    return api.tenantRequest('/maintenance/variant-value-move', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{
      ok: boolean;
      error?: string;
      dryRun: boolean;
      applied: boolean;
      fromAxisKey: string;
      fromValueKey: string;
      toAxisKey: string;
      toValueKey: string;
      wouldUpdateCount: number;
      skippedCount: number;
      conflictCount: number;
      usedNaCount?: number;
      wouldUpdateSample: string[];
      conflicts: Array<{ productId: string; sku: string; reason: string; detail: string | null }>;
      updatedCount?: number;
      rebuild?: { tagCount: number; productCount: number } | null;
    }>;
  },

  fetchMaintenanceVariantTags() {
    return api.tenantRequest('/maintenance/variant-tags') as Promise<{
      ok: true;
      collisionGroupCount: number;
      tags: Array<{
        axisKey: string;
        valueKey: string;
        axisLabelI18n: Record<string, string>;
        valueLabelI18n: Record<string, string>;
        productCount: number;
        labelKey: string;
        inCollision: boolean;
        isPlaceholder: boolean;
      }>;
    }>;
  },

  renameVariantValue(body: {
    dryRun: boolean;
    confirmSlug?: string;
    axisKey: string;
    fromValueKey: string;
    toValueKey?: string;
    labelI18n?: Record<string, string>;
    replaceLabels?: boolean;
  }) {
    return api.tenantRequest('/maintenance/variant-value-rename', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{
      ok: boolean;
      error?: string;
      dryRun: boolean;
      applied: boolean;
      wouldUpdateCount: number;
      conflictCount: number;
      wouldUpdateSample: string[];
      conflicts: Array<{ productId: string; sku: string; reason: string; detail: string | null }>;
      updatedCount?: number;
      rebuild?: { tagCount: number; productCount: number } | null;
    }>;
  },

  cleanupVariantAxes(body: {
    dryRun: boolean;
    confirmSlug?: string;
    focusAxis?: string | null;
    removeNa?: boolean;
  }) {
    return api.tenantRequest('/maintenance/variant-axes-cleanup', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{
      ok: boolean;
      error?: string;
      dryRun: boolean;
      applied: boolean;
      wouldUpdateCount: number;
      conflictCount: number;
      wouldUpdateSample: string[];
      conflicts: Array<{ productId: string; sku: string; reason: string; detail: string | null }>;
      updatedCount?: number;
      rebuild?: { tagCount: number; productCount: number } | null;
    }>;
  },

  clearTenantImportStaging(confirmSlug: string) {
    return api.tenantRequest('/maintenance/clear-import-staging', {
      method: 'POST',
      body: JSON.stringify({ confirmSlug }),
    }) as Promise<{
      ok: true;
      result: { deletedFiles: number };
    }>;
  },

  resetTenantClients(confirmSlug: string) {
    return api.tenantRequest('/maintenance/reset-clients', {
      method: 'POST',
      body: JSON.stringify({ confirmSlug }),
    }) as Promise<{
      ok: true;
      result: { deletedClients: number; deletedRegistrationRequests: number };
    }>;
  },

  async downloadTenantWarehouseBackup() {
    const res = await fetch('/api/tenant/maintenance/warehouse-backup', {
      method: 'POST',
      headers: { ...authHeaders(), ...tenantScopeHeaders() },
    });
    if (!res.ok) {
      const data = await parseJson(res);
      throw Object.assign(new Error(data.error || 'warehouse_backup_failed'), {
        data,
        status: res.status,
      });
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = /filename="([^"]+)"/i.exec(disposition);
    const filename = match?.[1] || `warehouse-backup-${Date.now()}.zip`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { filename, sizeBytes: blob.size };
  },

  async restoreTenantWarehouseBackup(body: {
    file: File;
    confirmSlug: string;
    restoreClients: boolean;
  }) {
    const form = new FormData();
    form.append('file', body.file);
    form.append('confirmSlug', body.confirmSlug);
    form.append('restoreClients', body.restoreClients ? 'true' : 'false');
    const res = await fetch('/api/tenant/maintenance/warehouse-restore', {
      method: 'POST',
      headers: tenantUploadHeaders(),
      body: form,
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw Object.assign(new Error(data.error || 'warehouse_restore_failed'), {
        data,
        status: res.status,
      });
    }
    return data as {
      ok: true;
      sourceSlug?: string;
      counts?: { products?: number; clientsRestored?: number };
    };
  },
};
